/**
 * Creative Engine - 7-Stage Workflow (I1 restructure, 2026-08-18)
 * Phase 3: Cipher
 *
 * Stages (non-linear — any stage is reachable from any other via the step
 * indicator or Previous/Next; each stage's state is fetched/persisted
 * independently by offer_id, so jumping around never discards work — I2):
 * 1. Psychology Map     - Generate & confirm audience psychology
 * 2. Angle              - Choose or create creative angle
 * 3. Hook               - Generate & select hooks
 * 4. Script             - Generate full scripts (sections)
 * 5. CTA                - Review/author CTA variants attached to scripts
 * 6. Creative Elements  - Set creative type per script, assemble modular
 *                         hook+body+CTA combinations (I4), submit to production
 * 7. Prompts            - Production/Veo prompt review, delivery, quality scoring
 *
 * Stages 5-7 were split out of what was previously step 4 (Script, which also
 * rendered CTA variants inline) and step 5 (Production, which mixed creative
 * type selection with the Veo prompt/delivery pipeline). No backend endpoint
 * changed — this is a frontend reorganization of the same 78 endpoints.
 */

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import axios from 'axios';
import ChatPanel from './ChatPanel';
import ExplorationPhase from './ExplorationPhase';
import { errorText } from './custom_creative_session/DevelopmentStage';
import { usePsychologyMap, ENTRY_TYPES } from '../services/usePsychologyMap';
import {
  resolveInitialStep, STEPS as SHARED_STEPS,
  STEP_PSYCHOLOGY, STEP_ANGLE, STEP_HOOKS, STEP_SCRIPTS,
  STEP_CTA, STEP_ELEMENTS, STEP_PROMPTS,
} from '../services/creativeSessionStep';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ABH_BU_ID = 'bu_dcb1830a0871';  // affiliate-only gate: exploration never shows for ABH

// Step indicator component — premium wizard style
// ── Session scoping (CREATIVE-SESSION-UX-FIXES item 7) ──────────────────────
// WHICH ROWS BELONG TO THIS SESSION. The implementation moved to
// `services/sessionScope.js` on 2026-09-09 so v2's `useAngles` hook can share
// it without importing this 3,400-line module and creating a cycle. Re-exported
// under the same name: every caller and every test that reads it from here is
// unaffected, and there is still exactly one copy of the rule.
export { scopeToSession } from '../services/sessionScope';
import { useAngles, ENTRY_POINTS, AWARENESS_STAGES } from '../services/useAngles';
import { useHooks } from '../services/useHooks';
import { useScripts } from '../services/useScripts';
import { useSessionTracks } from '../services/useSessionTracks';
import TrackSwitcher from './shared/TrackSwitcher';
import FormatTypePicker from './shared/FormatTypePicker';

// The line that tells an operator what is NOT on screen. Returns null when
// nothing was hidden, so a clean session shows no noise.
const HiddenRowsNote = ({ hiddenOtherSession, hiddenUnstamped, noun, testid }) => {
  if (!hiddenOtherSession && !hiddenUnstamped) return null;
  const parts = [];
  if (hiddenOtherSession) {
    parts.push(`${hiddenOtherSession} from other sessions on this offer`);
  }
  if (hiddenUnstamped) {
    parts.push(
      `${hiddenUnstamped} that predate session tracking (Cipher cannot tell which session made them)`
    );
  }
  return (
    <p className="text-slate-500 text-xs mt-2" data-testid={testid}>
      Not shown: {parts.join('; ')}. This view is scoped to the current session.
    </p>
  );
};

// The seven step components are EXPORTED as of 2026-09-09 (V2-AUTONOMOUS-WAVE2B)
// so v2's shell can mount the same components rather than growing a second copy
// of each. Additive: v1 renders them exactly as before, and nothing about their
// behaviour changed. Wave 3 replaces them one at a time behind the v2 shell;
// until it does, v1 and v2 show the same steps because they ARE the same steps.
const StepIndicator = ({ currentStep, steps }) => (
  <div className="flex items-center justify-between mb-10 px-2">
    {steps.map((step, idx) => (
      <div key={step.id} className="flex items-center">
        <div className={`wizard-step-indicator ${
          idx < currentStep 
            ? 'completed'
            : idx === currentStep
              ? 'active'
              : ''
        }`}>
          {idx < currentStep ? (
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
            </svg>
          ) : idx + 1}
        </div>
        <span className={`ml-3 text-sm font-medium ${
          idx === currentStep ? 'text-emerald-400' : idx < currentStep ? 'text-white' : 'text-slate-500'
        }`}>
          {step.label}
        </span>
        {idx < steps.length - 1 && (
          <div className={`w-12 h-0.5 mx-4 rounded-full ${
            idx < currentStep ? 'bg-emerald-500 shadow-[0_0_6px_rgba(0,212,212,0.4)]' : 'bg-slate-700'
          }`} />
        )}
      </div>
    ))}
  </div>
);

// ============================================================================
// ChatSourceBadge — Manual Session Mode (Phase 8)
// ----------------------------------------------------------------------------
// Renders a "💬 from chat" pill on any artifact (angle / hook / script / CTA)
// whose `source === "CHAT_DERIVED"`. Clicking pops a small detail panel
// showing the originating session_id + message_ids so the operator can
// trace the artifact back to the conversation that produced it.
// ============================================================================
const ChatSourceBadge = ({ artifact }) => {
  const [open, setOpen] = useState(false);
  if (!artifact || artifact.source !== 'CHAT_DERIVED') return null;
  const sessionId = artifact.derived_from_chat_session_id || '';
  const messageIds = artifact.derived_from_chat_message_ids || [];
  const shortSession = sessionId ? sessionId.slice(0, 16) : '?';
  return (
    <>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        title={`Chat-derived · session ${shortSession}`}
        className="px-2 py-0.5 bg-cyan-500/10 text-cyan-400 border border-cyan-500/30 rounded-full text-xs hover:bg-cyan-500/20 transition-colors"
        data-testid="chat-source-badge"
      >
        💬 from chat
      </button>
      {open && (
        <div
          className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
          onClick={() => setOpen(false)}
          data-testid="chat-source-modal"
        >
          <div
            className="bg-slate-900 border border-slate-700 rounded-xl max-w-md w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-white font-semibold mb-3">Chat-derived artifact</h3>
            <div className="space-y-3 text-sm">
              <div>
                <span className="text-slate-500 text-xs block mb-0.5">Session ID</span>
                <p className="text-white font-mono text-xs break-all">{sessionId || '—'}</p>
              </div>
              {messageIds.length > 0 && (
                <div>
                  <span className="text-slate-500 text-xs block mb-0.5">Source message{messageIds.length > 1 ? 's' : ''}</span>
                  <ul className="text-white font-mono text-xs space-y-0.5">
                    {messageIds.map(id => <li key={id} className="break-all">{id}</li>)}
                  </ul>
                </div>
              )}
              <div>
                <span className="text-slate-500 text-xs block mb-0.5">Locked by</span>
                <p className="text-white text-sm">{artifact.operator_id || '—'}</p>
              </div>
              <div>
                <span className="text-slate-500 text-xs block mb-0.5">Locked at</span>
                <p className="text-white text-sm">
                  {artifact.created_at ? new Date(artifact.created_at).toLocaleString() : '—'}
                </p>
              </div>
            </div>
            <p className="text-xs text-slate-500 mt-4 leading-relaxed">
              Open the Creative Hub and filter by this session ID to view the original
              conversation that produced this artifact.
            </p>
            <button
              onClick={() => setOpen(false)}
              className="mt-4 w-full px-4 py-2 bg-slate-700 text-white rounded-lg hover:bg-slate-600 text-sm"
              data-testid="chat-source-modal-close-btn"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </>
  );
};

// Psychology Map Step
export const PsychologyMapStep = ({ offer, onComplete, onRefresh, sessionId, isNewSession }) => {
  const {
    entries, loading, generating, feedback, setFeedback,
    awarenessStage, setAwarenessStage, mapNote, mapError,
    generateMap, confirmAndProceed, togglePriority, removeEntry,
    updateEntryEmotion,
  } = usePsychologyMap({ offer, isNewSession, onComplete });

  const entryTypes = ENTRY_TYPES;

  return (
    <div className="space-y-6" data-testid="psychology-map-step">
      {/* What the last run actually did. Silent when it did the obvious thing —
          a note on every generation is a note nobody reads. */}
      {/* Same fix on this surface: the alert is gone from the shared hook, so
          the error has to be rendered on BOTH screens or v1 would lose the
          reason entirely — a silent failed save is worse than a dialog. */}
      {mapError && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3"
             role="alert" data-testid="psychology-map-error">
          <p className="text-red-300 text-sm">{mapError}</p>
        </div>
      )}

      {mapNote && (
        <p className="text-xs text-amber-400" data-testid="psychology-map-note">
          {mapNote.text}
          {mapNote.count > 1 && (
            <span className="text-slate-500"> (asked {mapNote.count} times)</span>
          )}
        </p>
      )}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-white">Audience Psychology Map</h3>
          <p className="text-slate-400 text-sm">Generate psychological entry points for your target audience</p>
        </div>
        {entries.length === 0 ? (
          <button
            onClick={() => generateMap(false)}
            disabled={generating}
            data-testid="generate-psychology-map-btn"
            className="px-4 py-2 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 disabled:opacity-50"
          >
            {generating ? 'Generating...' : 'Generate Psychology Map'}
          </button>
        ) : (
          <button
            onClick={() => generateMap(true)}
            disabled={generating}
            data-testid="regenerate-psychology-map-btn"
            className="px-4 py-2 bg-slate-700 text-slate-300 rounded-lg hover:bg-slate-600 disabled:opacity-50"
          >
            {generating ? 'Regenerating...' : 'Regenerate'}
          </button>
        )}
      </div>

      {entries.length > 0 && (
        <>
          {/* Awareness Stage Selector */}
          <div className="bg-slate-800/50 rounded-lg p-4">
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Primary Awareness Stage Focus
            </label>
            <select
              value={awarenessStage}
              onChange={(e) => setAwarenessStage(e.target.value)}
              data-testid="awareness-stage-select"
              className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-2 text-white"
            >
              <option value="UNAWARE">Unaware - Don't know they have a problem</option>
              <option value="PROBLEM_AWARE">Problem Aware - Know the problem, not the solution</option>
              <option value="SOLUTION_AWARE">Solution Aware - Know solutions exist</option>
            </select>
          </div>

          {/* Entries grouped by type */}
          {entryTypes.map(type => {
            const typeEntries = entries.filter(e => e.entry_point_type === type);
            if (typeEntries.length === 0) return null;
            
            return (
              <div key={type} className="bg-slate-900/50 border border-slate-800 rounded-xl p-4">
                <h4 className="text-emerald-400 font-medium mb-3">
                  {type.replace('_', ' ')} ({typeEntries.length})
                </h4>
                <div className="space-y-2">
                  {typeEntries.map((entry, idx) => {
                    const globalIdx = entries.findIndex(e => e === entry);
                    return (
                      <div 
                        key={idx}
                        className={`p-3 rounded-lg border ${
                          entry.priority_flagged 
                            ? 'bg-emerald-500/10 border-emerald-500/30' 
                            : 'bg-slate-800/50 border-slate-700'
                        }`}
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <p className="text-white text-sm">{entry.statement}</p>
                            <div className="flex items-center gap-2 mt-2 text-xs text-slate-400">
                              <span className="px-2 py-0.5 bg-slate-700 rounded">{entry.awareness_stage}</span>
                              <select
                                value={entry.primary_emotion || ''}
                                onChange={(e) => updateEntryEmotion(globalIdx, e.target.value)}
                                className="px-2 py-0.5 bg-slate-700 border-none rounded text-xs text-slate-300 focus:ring-0"
                                data-testid={`emotion-select-${globalIdx}`}
                              >
                                <option value="">Emotion...</option>
                                {['FEAR', 'HOPE', 'FRUSTRATION', 'CURIOSITY', 'SHAME', 'ASPIRATION', 'ANGER', 'RELIEF', 'URGENCY', 'DESIRE'].map(em => (
                                  <option key={em} value={em}>{em}</option>
                                ))}
                              </select>
                              <span>{entry.demographic}</span>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => togglePriority(globalIdx)}
                              className={`px-2 py-1 text-xs rounded ${
                                entry.priority_flagged
                                  ? 'bg-emerald-500/20 text-emerald-400'
                                  : 'bg-slate-700 text-slate-400 hover:text-white'
                              }`}
                            >
                              {entry.priority_flagged ? '★ Priority' : '☆ Flag'}
                            </button>
                            <button
                              onClick={() => removeEntry(globalIdx)}
                              className="px-2 py-1 text-xs bg-red-500/10 text-red-400 rounded hover:bg-red-500/20"
                            >
                              Remove
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}

          {/* Feedback for regeneration */}
          <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-4">
            <label className="block text-sm font-medium text-slate-300 mb-2">Feedback / Direction</label>
            <textarea
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              placeholder="e.g., Focus more on fear of missing out, add entries targeting younger demographics..."
              className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-2 text-white text-sm placeholder-slate-500 resize-none"
              rows={2}
              data-testid="psychology-feedback-input"
            />
            <div className="flex justify-end mt-2">
              <button
                onClick={generateMap}
                disabled={generating}
                className="px-4 py-2 bg-slate-700 text-slate-300 rounded-lg hover:bg-slate-600 disabled:opacity-50 text-sm"
                data-testid="generate-more-psychology-btn"
              >
                {generating ? 'Generating...' : `Generate More${feedback ? ' (with feedback)' : ''}`}
              </button>
            </div>
          </div>

          {/* Continue */}
          <div className="flex justify-end pt-4">
            <button
              onClick={confirmAndProceed}
              disabled={loading || entries.length === 0}
              data-testid="confirm-psychology-map-btn"
              className="px-6 py-2 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 disabled:opacity-50"
            >
              {loading ? 'Saving...' : 'Continue'}
            </button>
          </div>
        </>
      )}
    </div>
  );
};

// Angle Selection Step — Multi-select with checkboxes + Wave 2 inline feedback
export const AngleSelectionStep = ({ offer, onComplete, onMultiAngleSelect, sessionId, isNewSession, sessionStartTime, refreshKey = 0 }) => {
  // WAVE 3, SCREEN 2. The behaviour lives in `services/useAngles.js` and is
  // shared with v2's screen, so the two presentations cannot decide differently.
  // What the sweep found before this was touched is listed in that file.
  const A = useAngles({ offer, sessionId, isNewSession, sessionStartTime,
                        onComplete, onMultiAngleSelect, refreshKey });
  const [competitorIntel, setCompetitorIntel] = useState(null);
  const [intelState, setIntelState] = useState('idle');

  // Per-angle drawer state stays local to the presentation — v2 draws these
  // differently and nothing about WHICH angle is open is a decision.
  const [refineOpenFor, setRefineOpenFor] = useState(null);
  const [refineFeedback, setRefineFeedback] = useState('');
  const [refiningAngleId, setRefiningAngleId] = useState(null);
  const [editOpenFor, setEditOpenFor] = useState(null);
  const [editDraft, setEditDraft] = useState({});
  const [savingEditFor, setSavingEditFor] = useState(null);
  const [editError, setEditError] = useState(null);

  useEffect(() => {
    const fetchIntel = async () => {
      const vertical = offer.vertical || offer.product_category || '';
      if (!vertical) { setIntelState('no-vertical'); return; }
      setIntelState('loading');
      try {
        const res = await axios.get(`${API}/swipekit/intelligence/${encodeURIComponent(vertical)}`);
        if (res.data.available) { setCompetitorIntel(res.data); setIntelState('ok'); }
        else setIntelState('none');
      } catch (e) {
        // Was `console.debug("Swipekit intelligence not available")` — which
        // said "not available" for an outage as readily as for an empty answer.
        setIntelState('failed');
      }
    };
    fetchIntel();
  }, [offer.vertical, offer.product_category]);

  const openEditor = (angle) => {
    setEditError(null);
    setEditOpenFor(angle.angle_id);
    // Seed from the angle as it stands, so an untouched field round-trips
    // unchanged rather than being blanked by an empty form.
    setEditDraft({
      angle_name: angle.angle_name || '',
      core_belief_shift: angle.core_belief_shift || '',
      psychological_entry_point: angle.psychological_entry_point || 'FRUSTRATION',
      target_awareness_stage: angle.target_awareness_stage || 'PROBLEM_AWARE',
      target_persona: angle.target_persona || '',
      angle_rationale: angle.angle_rationale || '',
    });
  };

  const saveEdits = async (angleId) => {
    setSavingEditFor(angleId);
    setEditError(null);
    const res = await A.saveAngleEdits(angleId, editDraft);
    setSavingEditFor(null);
    if (res.ok) { setEditOpenFor(null); setEditDraft({}); }
    else setEditError(res.error);
  };

  const runRefine = async (angleId) => {
    setRefiningAngleId(angleId);
    const ok = await A.refineAngle(angleId, refineFeedback);
    setRefiningAngleId(null);
    if (ok) { setRefineOpenFor(null); setRefineFeedback(''); }
  };

  const angles = A.angles;

  return (
    <div className="space-y-6" data-testid="angle-selection-step">
      {/* Competitor Intelligence Card (Swipekit) */}
      {competitorIntel && competitorIntel.available && (
        <div className="bg-gradient-to-r from-slate-900 to-slate-800/50 border border-cyan-800/30 rounded-xl p-5" data-testid="competitor-intel-card">
          <div className="flex items-center gap-2 mb-3">
            <span className="px-2 py-0.5 text-xs bg-cyan-500/20 text-cyan-400 rounded font-medium">Swipekit Intelligence</span>
            <span className="text-xs text-slate-500">{competitorIntel.ads_analyzed} ads analyzed in {competitorIntel.vertical}</span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div>
              <p className="text-xs text-slate-500 mb-1">Top Hooks</p>
              {(competitorIntel.top_hooks || []).slice(0, 3).map((h, i) => (
                <p key={i} className="text-xs text-slate-300">{h.item} <span className="text-slate-500">({h.count})</span></p>
              ))}
            </div>
            <div>
              <p className="text-xs text-slate-500 mb-1">Dominant Formats</p>
              {(competitorIntel.dominant_formats || []).slice(0, 3).map((f, i) => (
                <p key={i} className="text-xs text-slate-300">{f.item} <span className="text-slate-500">({f.count})</span></p>
              ))}
            </div>
            <div>
              <p className="text-xs text-slate-500 mb-1">Emotions Used</p>
              {(competitorIntel.top_emotions || []).slice(0, 3).map((e, i) => (
                <p key={i} className="text-xs text-slate-300">{e.item} <span className="text-slate-500">({e.count})</span></p>
              ))}
            </div>
            <div>
              <p className="text-xs text-slate-500 mb-1">Avg Ad Longevity</p>
              <p className="text-lg font-bold text-white">{competitorIntel.avg_ad_longevity_days}d</p>
              <p className="text-xs text-slate-500 mt-1">Structures</p>
              {(competitorIntel.common_structures || []).slice(0, 2).map((s, i) => (
                <p key={i} className="text-xs text-slate-300">{s.item}</p>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* An outage here is not "no competitor data". Said, not swallowed. */}
      {intelState === 'failed' && (
        <p className="text-amber-400/80 text-xs" data-testid="angle-intel-failed">
          Competitor intelligence could not be reached. The angles below are
          unaffected — this panel is the only thing missing.
        </p>
      )}

      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-white">Select Creative Angle</h3>
          <p className="text-slate-400 text-sm">
            Choose the strategic angle for your creative. <strong className="text-slate-300">Refine</strong> rewrites
            an angle from a note and keeps its psychology coherent — use it first.
            <strong className="text-slate-300"> Edit</strong> is there when you already know the exact words.
          </p>
        </div>
        <div className="flex gap-2">
          {angles.length === 0 && (
            <button
              onClick={A.generateAngles}
              disabled={A.generating || A.loadState === 'loading'}
              data-testid="generate-angles-btn"
              className="px-4 py-2 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 disabled:opacity-50"
            >
              {A.generating ? 'Generating...' : 'Generate Angles'}
            </button>
          )}
          {angles.length > 0 && (
            <button
              onClick={A.generateAngles}
              disabled={A.generating}
              data-testid="regenerate-angles-btn"
              className="px-4 py-2 bg-slate-700 text-slate-300 rounded-lg hover:bg-slate-600 disabled:opacity-50"
            >
              {A.generating ? 'Regenerating...' : 'Regenerate'}
            </button>
          )}
        </div>
      </div>

      {/* THE FOUR STATES, KEPT APART (rule 10). Before this every one of them
          was the same empty grid: still loading, the request failed, a new
          session that never asked, and an offer with genuinely no angles. */}
      {A.loadState === 'loading' && angles.length === 0 && (
        <p className="text-slate-400 text-sm" data-testid="angles-loading">Reading this offer's angles…</p>
      )}

      {A.loadState === 'failed' && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4" data-testid="angles-load-failed">
          <p className="text-red-300 text-sm">
            The angles could not be read: {A.loadError}
          </p>
          <p className="text-slate-400 text-xs mt-1">
            This is a failure, not an empty offer. Nothing has been lost.
          </p>
          <button
            onClick={A.retryLoad}
            className="mt-2 px-3 py-1 text-xs bg-slate-700 text-slate-200 rounded hover:bg-slate-600"
            data-testid="angles-retry-btn"
          >
            Try again
          </button>
        </div>
      )}

      {A.loadState === 'ok' && angles.length === 0 && (
        <p className="text-slate-400 text-sm" data-testid="angles-empty">
          {A.allCount > 0
            ? `This session has no angles yet. The offer holds ${A.allCount} from earlier work, not shown here.`
            : 'This offer has no angles yet. Generate a set to begin.'}
        </p>
      )}

      {A.note && (
        <p className="text-slate-300 text-sm" data-testid="angles-note">
          {A.note.text}{A.note.count > 1 ? ` (asked ${A.note.count} times)` : ''}
        </p>
      )}

      {A.actionError && (
        <p className="text-red-300 text-sm" role="alert" data-testid="angles-action-error">
          {A.actionError}
        </p>
      )}

      {/* ── "Create Custom" angle form — REMOVED 2026-09-08, item 4 ─────────
          Founder's ruling: redundant now that every generated angle carries
          Approve and Refine, and this build adds direct text editing on top of
          them. The form asked an operator to invent an angle from a blank page,
          next to five the engine had just written from the psychology map.

          Rule 8 clearance before removing the UI:
            bare name `showCustom`     — this component only; all uses removed here
            `createCustomAngle`        — this component only; removed here
            `customAngle` state        — this component only; removed here
            data-testid                — `toggle-custom-angle-btn`, asserted by no test
            the ENDPOINT it called     — `/offers/{id}/angles/custom-with-log` is
                                         KEPT. It is not this lane's to delete: it
                                         has its own router and service
                                         (`creative_feedback_wave2`), and removing
                                         a working endpoint because one caller went
                                         away is the deletion rule 8 exists to stop.
                                         Reported, not removed.

          2026-09-09, WAVE 3 SCREEN 2: that removal left its closing `)}` behind
          with no opening conditional. It parsed as JSXText, so React rendered
          the characters `)}` on screen under the buttons and had been doing so
          since. No test caught it because no test had ever RENDERED this
          component — every one of them read the source as a string.
      ────────────────────────────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {angles.map(angle => {
            const emotionStyles = {
              FEAR: 'bg-red-500/15 text-red-400 border-red-500/30',
              ASPIRATION: 'bg-blue-500/15 text-blue-400 border-blue-500/30',
              FRUSTRATION: 'bg-cyan-500/15 text-cyan-400 border-cyan-500/30',
              CURIOSITY: 'bg-purple-500/15 text-purple-400 border-purple-500/30',
              AUTHORITY: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
              SOCIAL_PROOF: 'bg-indigo-500/15 text-indigo-400 border-indigo-500/30',
            };

            return (
              <div
                key={angle.angle_id}
                className={`rounded-xl border cursor-pointer transition-all hover:scale-[1.01] ${
                  A.selectedAngles.includes(angle.angle_id)
                    ? 'bg-emerald-500/10 border-emerald-500 ring-1 ring-emerald-500/30'
                    : 'bg-slate-900/50 border-slate-800 hover:border-slate-600'
                }`}
                onClick={() => A.toggleAngle(angle.angle_id)}
                data-testid={`angle-card-${angle.angle_id}`}
              >
                {/* Checkbox indicator */}
                <div className="p-5 pb-3">
                  <div className="flex items-center gap-2 mb-3">
                    <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                      A.selectedAngles.includes(angle.angle_id)
                        ? 'bg-emerald-500 border-emerald-500'
                        : 'border-slate-600'
                    }`}>
                      {A.selectedAngles.includes(angle.angle_id) && (
                        <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </div>
                    <span className={`px-2.5 py-1 text-xs font-semibold rounded-full border ${
                      emotionStyles[angle.psychological_entry_point] || 'bg-slate-500/15 text-slate-400 border-slate-500/30'
                    }`}>
                      {angle.psychological_entry_point}
                    </span>
                    <span className="text-xs text-slate-500">{angle.target_awareness_stage}</span>
                  </div>
                  <h4 className="text-white font-semibold text-base leading-snug">{angle.angle_name}</h4>
                </div>

                {/* Body — short description */}
                <div className="px-5 pb-4">
                  <p className="text-slate-400 text-sm leading-relaxed line-clamp-2">{angle.core_belief_shift}</p>
                </div>

                {/* Footer metadata */}
                <div className="px-5 pb-4 flex items-center gap-3 text-xs text-slate-500 flex-wrap">
                  {angle.target_persona && <span>{angle.target_persona}</span>}
                  {angle.angle_risk && (
                    <span className="text-purple-400">{angle.angle_risk}</span>
                  )}
                  {angle.angle_memory_context && (
                    <span
                      className="px-2 py-0.5 bg-emerald-500/10 text-emerald-400 border border-emerald-500/30 rounded-full"
                      data-testid={`angle-intel-chip-${angle.angle_id}`}
                      title={angle.angle_memory_context}
                    >
                      Intelligence-backed
                    </span>
                  )}
                  {angle.edited_at && (
                    <span
                      className="px-2 py-0.5 text-[10px] rounded bg-slate-600/30 text-slate-300"
                      data-testid={`angle-edited-chip-${angle.angle_id}`}
                    >
                      edited by hand
                    </span>
                  )}
                  {angle.refined_at && (
                    <span
                      className="px-2 py-0.5 bg-cyan-500/10 text-cyan-400 border border-cyan-500/30 rounded-full"
                      data-testid={`angle-refined-chip-${angle.angle_id}`}
                    >
                      Refined
                    </span>
                  )}
                  <ChatSourceBadge artifact={angle} />
                </div>

                {/* Wave 2: Per-angle inline actions */}
                <div
                  className="px-5 pb-4 flex items-center gap-2 flex-wrap"
                  onClick={(e) => e.stopPropagation()}
                  data-testid={`angle-actions-${angle.angle_id}`}
                >
                  {/* ── Per-card "Approve" — REMOVED 2026-09-09 ─────────────
                      It called `proceed([thisAngle])`, which committed that one
                      angle and advanced the step while IGNORING every checked
                      box. Before MA-3 that was a harmless shortcut. Now that
                      checking three boxes genuinely opens three sessions, two
                      controls that disagree is a way to lose two of them by
                      accident — the Founder hit exactly that.

                      There is ONE way to proceed now: tick the angles, press
                      Continue. Approving a single angle is the same gesture
                      with one box ticked, so nothing is lost.

                      Rule 8 clearance: `approveAngle` was already gone (MA-3
                      folded it into `A.proceed`); the testid
                      `angle-approve-btn-*` was asserted by one test, updated
                      with this change; `A.proceed(ids)` KEEPS its optional
                      argument — the hook's contract is shared with v2 and a
                      caller passing explicit ids is legitimate.
                  ───────────────────────────────────────────────────────── */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setRefineOpenFor(refineOpenFor === angle.angle_id ? null : angle.angle_id);
                      setRefineFeedback('');
                    }}
                    className="px-3 py-1 text-xs bg-slate-700 text-slate-300 rounded hover:bg-slate-600"
                    data-testid={`angle-refine-toggle-${angle.angle_id}`}
                  >
                    {refineOpenFor === angle.angle_id ? 'Cancel' : 'Refine'}
                  </button>
                  {/* ITEM 4 — direct editing, deliberately the quieter of the
                      two. Refine is the encouraged default and keeps its
                      primary-ish styling; this is a plain link-weight control
                      for the operator who already has the words. */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      if (editOpenFor === angle.angle_id) {
                        setEditOpenFor(null);
                        setEditDraft({});
                      } else {
                        openEditor(angle);
                      }
                    }}
                    className="px-3 py-1 text-xs text-slate-400 rounded hover:text-slate-200 hover:bg-slate-800"
                    data-testid={`angle-edit-toggle-${angle.angle_id}`}
                  >
                    {editOpenFor === angle.angle_id ? 'Cancel' : 'Edit text'}
                  </button>
                </div>

                {/* ITEM 4 — the direct editor. Every field the refine path can
                    change, editable by hand. Omitting a field leaves it alone;
                    the draft is seeded from the angle so that holds. */}
                {editOpenFor === angle.angle_id && (
                  <div
                    className="px-5 pb-5 border-t border-slate-800 pt-4 bg-slate-950/40 space-y-3"
                    onClick={(e) => e.stopPropagation()}
                    data-testid={`angle-edit-drawer-${angle.angle_id}`}
                  >
                    <p className="text-xs text-slate-500">
                      Editing this angle's text directly. Refine usually gives a
                      better-integrated result — this is here for when you know
                      exactly what you want it to say.
                    </p>

                    {editError && (
                      <p className="text-amber-400 text-xs" data-testid={`angle-edit-error-${angle.angle_id}`}>
                        {editError}
                      </p>
                    )}

                    <div>
                      <label className="block text-xs text-slate-400 mb-1">Angle name</label>
                      <input
                        type="text"
                        value={editDraft.angle_name ?? ''}
                        onChange={(e) => setEditDraft(d => ({ ...d, angle_name: e.target.value }))}
                        className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm"
                        data-testid={`angle-edit-name-${angle.angle_id}`}
                      />
                    </div>

                    <div>
                      <label className="block text-xs text-slate-400 mb-1">Core belief shift</label>
                      <textarea
                        value={editDraft.core_belief_shift ?? ''}
                        onChange={(e) => setEditDraft(d => ({ ...d, core_belief_shift: e.target.value }))}
                        rows={3}
                        className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm resize-none"
                        data-testid={`angle-edit-belief-${angle.angle_id}`}
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs text-slate-400 mb-1">Entry point</label>
                        <select
                          value={editDraft.psychological_entry_point ?? 'FRUSTRATION'}
                          onChange={(e) => setEditDraft(d => ({ ...d, psychological_entry_point: e.target.value }))}
                          className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm"
                          data-testid={`angle-edit-entry-${angle.angle_id}`}
                        >
                          {ENTRY_POINTS.map(ep => <option key={ep} value={ep}>{ep}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs text-slate-400 mb-1">Awareness stage</label>
                        <select
                          value={editDraft.target_awareness_stage ?? 'PROBLEM_AWARE'}
                          onChange={(e) => setEditDraft(d => ({ ...d, target_awareness_stage: e.target.value }))}
                          className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm"
                          data-testid={`angle-edit-stage-${angle.angle_id}`}
                        >
                          {AWARENESS_STAGES.map(a => <option key={a} value={a}>{a}</option>)}
                        </select>
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs text-slate-400 mb-1">Target persona</label>
                      <input
                        type="text"
                        value={editDraft.target_persona ?? ''}
                        onChange={(e) => setEditDraft(d => ({ ...d, target_persona: e.target.value }))}
                        className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm"
                        data-testid={`angle-edit-persona-${angle.angle_id}`}
                      />
                    </div>

                    <div>
                      <label className="block text-xs text-slate-400 mb-1">Rationale</label>
                      <textarea
                        value={editDraft.angle_rationale ?? ''}
                        onChange={(e) => setEditDraft(d => ({ ...d, angle_rationale: e.target.value }))}
                        rows={2}
                        className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm resize-none"
                        data-testid={`angle-edit-rationale-${angle.angle_id}`}
                      />
                    </div>

                    <div className="flex justify-end gap-2">
                      <button
                        onClick={(e) => { e.stopPropagation(); setEditOpenFor(null); setEditDraft({}); }}
                        className="px-3 py-1.5 text-xs bg-slate-700 text-slate-300 rounded hover:bg-slate-600"
                        data-testid={`angle-edit-cancel-${angle.angle_id}`}
                      >
                        Discard
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); saveEdits(angle.angle_id); }}
                        disabled={
                          savingEditFor === angle.angle_id ||
                          !(editDraft.angle_name || '').trim() ||
                          !(editDraft.core_belief_shift || '').trim()
                        }
                        className="px-3 py-1.5 text-xs bg-emerald-500 text-white rounded hover:bg-emerald-600 disabled:opacity-50"
                        data-testid={`angle-edit-save-${angle.angle_id}`}
                      >
                        {savingEditFor === angle.angle_id ? 'Saving…' : 'Save edits'}
                      </button>
                    </div>
                  </div>
                )}

                {/* Wave 2: Inline refine drawer */}
                {refineOpenFor === angle.angle_id && (
                  <div
                    className="px-5 pb-5 border-t border-slate-800 pt-4 bg-slate-950/40"
                    onClick={(e) => e.stopPropagation()}
                    data-testid={`angle-refine-drawer-${angle.angle_id}`}
                  >
                    <label className="block text-xs text-slate-400 mb-2">
                      What should change about this angle?
                    </label>
                    <textarea
                      value={refineFeedback}
                      onChange={(e) => setRefineFeedback(e.target.value)}
                      placeholder="e.g., Make the belief shift sharper, target younger audience, less fear-based..."
                      rows={2}
                      className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm placeholder-slate-500 resize-none"
                      data-testid={`angle-refine-input-${angle.angle_id}`}
                    />
                    <div className="flex justify-end mt-2">
                      <button
                        onClick={(e) => { e.stopPropagation(); runRefine(angle.angle_id); }}
                        disabled={refiningAngleId === angle.angle_id || refineFeedback.trim().length < 3}
                        className="px-3 py-1.5 text-xs bg-emerald-500 text-white rounded hover:bg-emerald-600 disabled:opacity-50"
                        data-testid={`angle-refine-submit-${angle.angle_id}`}
                      >
                        {refiningAngleId === angle.angle_id ? 'Refining…' : 'Refine angle'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
      </div>

      <HiddenRowsNote
        hiddenOtherSession={A.hiddenOtherSession}
        hiddenUnstamped={A.hiddenUnstamped}
        noun="angles"
        testid="angles-hidden-note"
      />

      {/* Every angle in production predates session stamping. Showing them is
          right; pretending they are this session's work is not. */}
      {A.predateNote > 0 && (
        <p className="text-slate-500 text-xs" data-testid="angles-predate-note">
          Showing {A.predateNote} angle{A.predateNote > 1 ? 's' : ''} from this
          offer that predate session tracking — Cipher cannot tell which session
          made them. Angles generated from now on carry their session.
        </p>
      )}

      {/* Feedback for regeneration */}
      {angles.length > 0 && (
        <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-4">
          <label className="block text-sm font-medium text-slate-300 mb-2">Reject all and regenerate with feedback</label>
          <textarea
            value={A.feedback}
            onChange={(e) => A.setFeedback(e.target.value)}
            placeholder="e.g., More aggressive angles, focus on scarcity, add a social proof angle..."
            className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-2 text-white text-sm placeholder-slate-500 resize-none"
            rows={2}
            data-testid="angles-feedback-input"
          />
          <div className="flex justify-end mt-2">
            <button
              onClick={A.generateAngles}
              disabled={A.generating}
              className="px-4 py-2 bg-slate-700 text-slate-300 rounded-lg hover:bg-slate-600 disabled:opacity-50 text-sm"
              data-testid="generate-more-angles-btn"
            >
              {A.generating ? 'Generating...' : A.feedback ? 'Regenerate with feedback' : 'Generate more'}
            </button>
          </div>
        </div>
      )}

      {/* Continue button for multi-angle selection */}
      {angles.length > 0 && (
        <div className="pt-4 border-t border-slate-800">
          {/* MA-3, 2026-09-09. This used to warn that the extra angles lived in
              memory and would not survive a reload — true then, and no longer.
              Each angle now gets a real session of its own. */}
          {A.multiAngleWarning && (
            <p className="text-slate-300 text-xs mb-2" data-testid="angles-multi-warning">
              Continuing will open {A.selectedAngles.length} creative sessions —
              one per angle. Each runs its own hooks, scripts, CTA and prompts,
              and can be finished and submitted on its own.
            </p>
          )}
          <div className="flex items-center justify-between">
            <p className="text-sm text-slate-400">
              {A.selectedAngles.length === 0
                ? 'Select one or more angles to continue'
                : `${A.selectedAngles.length} angle${A.selectedAngles.length > 1 ? 's' : ''} selected`}
            </p>
            <button
              onClick={() => A.proceed()}
              disabled={A.selecting || A.selectedAngles.length === 0}
              data-testid="confirm-angles-btn"
              className="px-6 py-2 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 disabled:opacity-50"
            >
              {A.selecting ? 'Selecting...' : `Continue with ${A.selectedAngles.length} Angle${A.selectedAngles.length > 1 ? 's' : ''}`}
            </button>
          </div>
        </div>
      )}

    </div>
  );
};
export const HookSelectionStep = ({ offer, onComplete, sessionId, isNewSession, sessionStartTime, angleId, refreshKey = 0 }) => {
  // WAVE 3, SCREEN 3. Behaviour lives in `services/useHooks.js`, shared with
  // v2's screen. What the sweep found before this was touched is listed there.
  const H = useHooks({ offer, sessionId, isNewSession, sessionStartTime,
                       angleId, onComplete, refreshKey });

  const hookTypeColors = {
    'CURIOSITY_GAP': 'bg-purple-500/20 text-purple-400',
    'CONTRARIAN': 'bg-red-500/20 text-red-400',
    'HIGH_EMOTION': 'bg-orange-500/20 text-orange-400',
    'BOLD_CLAIM': 'bg-blue-500/20 text-blue-400',
    'DIRECT_ADDRESS': 'bg-emerald-500/20 text-emerald-400',
    'STORY_OPEN': 'bg-pink-500/20 text-pink-400',
  };

  return (
    <div className="space-y-6" data-testid="hook-selection-step">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-white">Select Hooks</h3>
          <p className="text-slate-400 text-sm">Choose hooks to use in your scripts (select multiple)</p>
        </div>
        {H.hooks.length === 0 && (
          <button
            onClick={H.generateHooks}
            disabled={H.generating || H.loadState === 'loading'}
            data-testid="generate-hooks-btn"
            className="px-4 py-2 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 disabled:opacity-50"
          >
            {H.generating ? 'Generating...' : 'Generate Hooks'}
          </button>
        )}
      </div>

      {/* THE FOUR STATES, KEPT APART (rule 10). They were one blank list. */}
      {H.loadState === 'loading' && H.hooks.length === 0 && (
        <p className="text-slate-400 text-sm" data-testid="hooks-loading">Reading this offer's hooks…</p>
      )}

      {H.loadState === 'failed' && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4" data-testid="hooks-load-failed">
          <p className="text-red-300 text-sm">The hooks could not be read: {H.loadError}</p>
          <p className="text-slate-400 text-xs mt-1">This is a failure, not an empty offer. Nothing has been lost.</p>
          <button
            onClick={H.retryLoad}
            className="mt-2 px-3 py-1 text-xs bg-slate-700 text-slate-200 rounded hover:bg-slate-600"
            data-testid="hooks-retry-btn"
          >
            Try again
          </button>
        </div>
      )}

      {H.loadState === 'ok' && H.hooks.length === 0 && (
        <p className="text-slate-400 text-sm" data-testid="hooks-empty">
          {H.allCount > 0
            ? `This session has no hooks yet. The offer holds ${H.allCount} from earlier work, not shown here.`
            : 'No hooks yet for this offer. Generate a set to begin.'}
        </p>
      )}

      {H.note && (
        <p className="text-slate-300 text-sm" data-testid="hooks-note">
          {H.note.text}{H.note.count > 1 ? ` (asked ${H.note.count} times)` : ''}
        </p>
      )}

      {H.actionError && (
        <p className="text-red-300 text-sm" role="alert" data-testid="hooks-action-error">
          {H.actionError}
        </p>
      )}

      <div className="space-y-3">
        {H.hooks.map(hook => (
          <div
            key={hook.element_id}
            className={`p-4 rounded-xl border cursor-pointer transition-all ${
              H.selectedHooks.includes(hook.element_id)
                ? 'bg-emerald-500/10 border-emerald-500'
                : 'bg-slate-900/50 border-slate-800 hover:border-slate-600'
            }`}
            onClick={() => H.toggleHook(hook.element_id)}
            data-testid={`hook-card-${hook.element_id}`}
          >
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2 flex-wrap">
                  <span className="text-emerald-400 font-mono text-sm" title={hook.element_number ? undefined : 'Not yet promoted to the library'}>{hook.element_number || '—'}</span>
                  <span className={`px-2 py-0.5 text-xs rounded ${hookTypeColors[hook.hook_type] || 'bg-slate-500/20 text-slate-400'}`}>
                    {hook.hook_type?.replace('_', ' ')}
                  </span>
                  <span className="px-2 py-0.5 text-xs rounded bg-slate-700 text-slate-400">
                    {hook.platform_optimisation}
                  </span>
                  <ChatSourceBadge artifact={hook} />
                </div>
                <p className="text-white">"{hook.content}"</p>
                {hook.hook_rationale && (
                  <p className="text-slate-500 text-sm mt-2">{hook.hook_rationale}</p>
                )}
              </div>
              <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                H.selectedHooks.includes(hook.element_id)
                  ? 'bg-emerald-500 border-emerald-500 text-white'
                  : 'border-slate-600'
              }`}>
                {H.selectedHooks.includes(hook.element_id) && '✓'}
              </div>
            </div>
          </div>
        ))}
      </div>

      <HiddenRowsNote
        hiddenOtherSession={H.hiddenOtherSession}
        hiddenUnstamped={H.hiddenUnstamped}
        noun="hooks"
        testid="hooks-hidden-note"
      />

      {H.predateNote > 0 && (
        <p className="text-slate-500 text-xs" data-testid="hooks-predate-note">
          Showing {H.predateNote} hook{H.predateNote > 1 ? 's' : ''} from this
          offer that predate session tracking — Cipher cannot tell which session
          made them.
        </p>
      )}

      {/* Feedback for regeneration */}
      {H.hooks.length > 0 && (
        <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-4">
          <label className="block text-sm font-medium text-slate-300 mb-2">Feedback / Direction</label>
          <textarea
            value={H.feedback}
            onChange={(e) => H.setFeedback(e.target.value)}
            placeholder="e.g., More curiosity-driven hooks, shorter punchier openings..."
            className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-2 text-white text-sm placeholder-slate-500 resize-none"
            rows={2}
            data-testid="hooks-feedback-input"
          />
          <div className="flex justify-end mt-2">
            <button
              onClick={H.generateHooks}
              disabled={H.generating}
              className="px-4 py-2 bg-slate-700 text-slate-300 rounded-lg hover:bg-slate-600 disabled:opacity-50 text-sm"
              data-testid="generate-more-hooks-btn"
            >
              {H.generating ? 'Generating...' : `Generate More${H.feedback ? ' (with feedback)' : ''}`}
            </button>
          </div>
        </div>
      )}

      {H.hooks.length > 0 && (
        <div className="flex justify-between items-center pt-4">
          <span className="text-slate-400 text-sm">
            {H.selectedHooks.length} hook{H.selectedHooks.length !== 1 ? 's' : ''} selected
          </span>
          <button
            onClick={H.confirmHooks}
            disabled={H.saving || H.selectedHooks.length === 0}
            data-testid="confirm-hooks-btn"
            className="px-6 py-2 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 disabled:opacity-50"
          >
            {H.saving ? 'Saving...' : 'Continue'}
          </button>
        </div>
      )}
    </div>
  );
};

export const ScriptGenerationStep = ({ offer, onComplete, sessionId, isNewSession, sessionStartTime, angleId, refreshKey = 0 }) => {
  // WAVE 3, SCREEN 4. Behaviour moved to `services/useScripts.js`, shared with
  // v2's ScriptStep. The local names below are rebound to it so v1's markup is
  // untouched — the sweep's fixes reach this screen without a re-layout.
  const S = useScripts({ offer, sessionId, isNewSession, sessionStartTime,
                         angleId, onComplete, refreshKey });
  const scripts = S.scripts;
  const generating = S.generating;
  const platform = S.platform, setPlatform = S.setPlatform;
  const scriptLength = S.scriptLength, setScriptLength = S.setScriptLength;
  const feedback = S.feedback, setFeedback = S.setFeedback;
  const generateScripts = S.generateScripts;

  const [expandedScript, setExpandedScript] = useState(null);
  const [pushbackKey, setPushbackKey] = useState(null);
  const [pushbackText, setPushbackText] = useState('');
  const [explainKey, setExplainKey] = useState(null);
  const [explanations, setExplanations] = useState({});
  const [explainFailed, setExplainFailed] = useState({});
  const pushbackLoading = S.busyScript;
  const explainLoading = null;
  const [scriptConfirmFailures, setScriptConfirmFailures] = useState([]);
  const loading = S.loadState === 'loading';

  const rewriteSection = async (scriptId, section) => {
    const ok = await S.rewriteSection(scriptId, section, pushbackText);
    if (ok) { setPushbackKey(null); setPushbackText(''); }
  };

  const explainSection = async (scriptId, section) => {
    const key = `${scriptId}::${section}`;
    setExplainKey(key);
    const res = await S.explainSection(scriptId, section);
    // A FAILURE IS NOT AN EXPLANATION. The old code wrote "Could not load
    // reasoning right now." into `explanations`, so a failed read rendered in
    // the same slot and the same style as real reasoning.
    if (res.ok) {
      setExplanations(prev => ({ ...prev, [key]: res.text }));
      setExplainFailed(prev => ({ ...prev, [key]: null }));
    } else {
      setExplanations(prev => ({ ...prev, [key]: null }));
      setExplainFailed(prev => ({ ...prev, [key]: res.text }));
    }
  };

  // `S.approveScript` stays in the hook — it is the single-script call, still
  // used by nothing on screen now. Not deleted: rule 8, and it is one line.

  return (
    <div className="space-y-6" data-testid="script-generation-step">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-white">Generated Scripts</h3>
          <p className="text-slate-400 text-sm">Review scripts and rewrite sections. Creative type is set on the Creative Elements step, where the script is submitted.</p>
        </div>
        {scripts.length === 0 && (
          <div className="flex items-center gap-2">
            <select
              value={platform}
              onChange={(e) => setPlatform(e.target.value)}
              data-testid="platform-select"
              className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white"
            >
              <option value="META">Meta</option>
              <option value="TIKTOK">TikTok</option>
            </select>
            <select
              value={scriptLength}
              onChange={(e) => setScriptLength(e.target.value)}
              data-testid="script-length-select"
              className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white"
            >
              <option value="15-30">15-30 sec</option>
              <option value="30-45">30-45 sec</option>
              <option value="45-60">45-60 sec</option>
            </select>
            <button
              onClick={generateScripts}
              disabled={generating}
              data-testid="generate-scripts-btn"
              className="px-4 py-2 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 disabled:opacity-50"
            >
              {generating ? 'Generating...' : 'Generate Scripts'}
            </button>
          </div>
        )}
      </div>

      <div className="space-y-4">
        {scripts.map((script, idx) => (
          <div
            key={script.script_id}
            className="bg-slate-900/50 border border-slate-800 rounded-xl overflow-hidden"
          >
            {/* Script Header */}
            <div 
              className="p-4 flex items-center justify-between cursor-pointer hover:bg-slate-800/30"
              onClick={() => setExpandedScript(expandedScript === script.script_id ? null : script.script_id)}
            >
              <div className="flex items-center gap-3">
                <span className="text-emerald-400 font-mono">{script.body_element_number || `Script ${idx + 1}`}</span>
                <span className="text-white font-medium">
                  {script.platform} • {script.total_duration_seconds}s
                </span>
                {script.compliance_flags?.length > 0 && (
                  <span className="px-2 py-0.5 text-xs bg-amber-500/20 text-amber-400 rounded">
                    {script.compliance_flags.length} compliance note{script.compliance_flags.length > 1 ? 's' : ''}
                  </span>
                )}
                {script.status && (
                  <span className={`px-2 py-0.5 text-xs rounded ${
                    script.status === 'APPROVED_FOR_PRODUCTION' ? 'bg-emerald-500/20 text-emerald-400' :
                    script.status === 'PENDING_APPROVAL' ? 'bg-blue-500/20 text-blue-400' :
                    script.status === 'CHANGES_REQUESTED' ? 'bg-amber-500/20 text-amber-400' :
                    'bg-slate-500/20 text-slate-400'
                  }`}>
                    {script.status.replace(/_/g, ' ')}
                  </span>
                )}
                <ChatSourceBadge artifact={script} />
              </div>
              <div className="flex items-center gap-2">
                {/* Creative type is set in the Creative Elements stage, not here — I1 */}
                {script.creative_type && (
                  <span className={`px-2 py-0.5 text-xs rounded ${
                    script.creative_type === 'AI' ? 'bg-purple-500/20 text-purple-400' :
                    script.creative_type === 'VE' ? 'bg-blue-500/20 text-blue-400' :
                    'bg-pink-500/20 text-pink-400'
                  }`}>{script.creative_type === 'AI' ? 'AI Video' : script.creative_type}</span>
                )}
                <span className="text-slate-400">
                  {expandedScript === script.script_id ? '▲' : '▼'}
                </span>
              </div>
            </div>

            {/* Expanded Content */}
            {expandedScript === script.script_id && (
              <div className="border-t border-slate-800 p-4 space-y-4">
                {/* Wave 2: Approve full script bar */}
                <div className="flex items-center justify-between bg-slate-800/40 rounded-lg p-3" data-testid={`approve-script-bar-${script.script_id}`}>
                  {/* FOUNDER RULING 2026-09-09 (CREATIVE-NO-FOUNDER-GATE):
                      the approval step comes out of the creative process on
                      BOTH surfaces, "Autonomous and Custom alike" — so v1's
                      per-script approve button is gone too, and its two real
                      side effects moved to this step's Confirm & continue at
                      the foot. ABH still runs on this screen, which is why it
                      was not left behind for wave 5 to deal with. */}
                  <div className="text-sm">
                    {script.approved_for_production ? (
                      <span className="text-emerald-400" data-testid={`script-approved-label-${script.script_id}`}>
                        Confirmed — permanent numbers minted, script saved to Drive
                      </span>
                    ) : (
                      <span className="text-slate-400">
                        Review the script and push back on any section. Confirm &
                        continue at the foot of the step.
                      </span>
                    )}
                  </div>
                </div>

                {/* Compliance Flags */}
                {script.compliance_flags?.length > 0 && (
                  <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3">
                    <p className="text-amber-400 font-medium text-sm mb-2">Compliance Notes:</p>
                    <ul className="text-amber-300 text-sm space-y-1">
                      {script.compliance_flags.map((flag, i) => (
                        <li key={i}>• {flag}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Script Sections */}
                <div className="space-y-3">
                  {script.sections?.map((section, i) => {
                    const secKey = `${script.script_id}::${section.section}`;
                    return (
                    <div key={i} className="bg-slate-800/50 rounded-lg p-3" data-testid={`script-section-${script.script_id}-${section.section}`}>
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        <span className={`px-2 py-0.5 text-xs rounded font-medium ${
                          section.section === 'HOOK' ? 'bg-purple-500/20 text-purple-400' :
                          section.section === 'TENSION' ? 'bg-red-500/20 text-red-400' :
                          section.section === 'ESCALATION' ? 'bg-orange-500/20 text-orange-400' :
                          section.section === 'REVEAL' ? 'bg-blue-500/20 text-blue-400' :
                          section.section === 'PROOF' ? 'bg-emerald-500/20 text-emerald-400' :
                          section.section === 'CTA' ? 'bg-pink-500/20 text-pink-400' :
                          'bg-slate-500/20 text-slate-400'
                        }`}>
                          {section.section}
                        </span>
                        <span className="text-slate-500 text-xs">{section.timing}</span>
                        {script.section_edits?.[section.section]?.last_edited_at && (
                          <span className="px-2 py-0.5 text-xs bg-cyan-500/10 text-cyan-400 border border-cyan-500/30 rounded-full" data-testid={`section-edited-chip-${script.script_id}-${section.section}`}>
                            Edited
                          </span>
                        )}
                        <div className="ml-auto flex items-center gap-1.5">
                          <button
                            onClick={() => {
                              setPushbackKey(pushbackKey === secKey ? null : secKey);
                              setPushbackText('');
                            }}
                            className="px-2 py-0.5 text-xs bg-slate-700 text-slate-300 rounded hover:bg-slate-600"
                            data-testid={`section-pushback-toggle-${script.script_id}-${section.section}`}
                          >
                            {pushbackKey === secKey ? 'Cancel' : 'Push back'}
                          </button>
                          <button
                            onClick={() => explainSection(script.script_id, section.section)}
                            disabled={explainLoading === secKey}
                            className="px-2 py-0.5 text-xs bg-slate-700 text-slate-300 rounded hover:bg-slate-600 disabled:opacity-50"
                            data-testid={`section-explain-btn-${script.script_id}-${section.section}`}
                          >
                            {explainLoading === secKey ? '…' : 'Explain'}
                          </button>
                        </div>
                      </div>
                      <p className="text-white text-sm" data-testid={`section-spoken-${script.script_id}-${section.section}`}>{section.spoken_text}</p>
                      {section.visual_direction && (
                        <p className="text-slate-500 text-xs mt-2 italic">
                          Visual: {section.visual_direction}
                        </p>
                      )}

                      {/* Inline pushback drawer */}
                      {pushbackKey === secKey && (
                        <div
                          className="mt-3 pt-3 border-t border-slate-700"
                          data-testid={`section-pushback-drawer-${script.script_id}-${section.section}`}
                        >
                          <label className="block text-xs text-slate-400 mb-1.5">Rewrite this section — what should change?</label>
                          <textarea
                            value={pushbackText}
                            onChange={(e) => setPushbackText(e.target.value)}
                            placeholder={`e.g., Make the ${section.section.toLowerCase()} sharper, less cliché, more specific...`}
                            rows={2}
                            className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm placeholder-slate-500 resize-none"
                            data-testid={`section-pushback-input-${script.script_id}-${section.section}`}
                          />
                          <div className="flex justify-end mt-1.5">
                            <button
                              onClick={() => rewriteSection(script.script_id, section.section)}
                              disabled={pushbackLoading === secKey || pushbackText.trim().length < 3}
                              className="px-3 py-1 text-xs bg-emerald-500 text-white rounded hover:bg-emerald-600 disabled:opacity-50"
                              data-testid={`section-pushback-submit-${script.script_id}-${section.section}`}
                            >
                              {pushbackLoading === secKey ? 'Rewriting…' : 'Rewrite section'}
                            </button>
                          </div>
                        </div>
                      )}

                      {/* Inline explain drawer */}
                      {explainKey === secKey && explanations[secKey] && (
                        <div
                          className="mt-3 pt-3 border-t border-slate-700 bg-slate-900/40 rounded-b-lg -mx-3 -mb-3 px-3 pb-3"
                          data-testid={`section-explain-drawer-${script.script_id}-${section.section}`}
                        >
                          <p className="text-xs text-slate-400 font-medium mb-1.5">Reasoning</p>
                          <p className="text-slate-300 text-sm leading-relaxed whitespace-pre-line">
                            {explanations[secKey]}
                          </p>
                        </div>
                      )}
                    </div>
                    );
                  })}
                </div>

                {/* The CTA count badge that stood here read `script.cta_variants`,
                    which affiliate scripts never carry (see the CTAStep header),
                    so it could not render for any script and never had. Removed
                    rather than repointed: this step does not load CTA elements,
                    and the CTA stage now shows the real per-script counts. No
                    behaviour is lost — it was already invisible. */}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Feedback for regeneration */}
      {scripts.length > 0 && (
        <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-4">
          <label className="block text-sm font-medium text-slate-300 mb-2">Regenerate entire script set with feedback</label>
          <textarea
            value={feedback}
            onChange={(e) => setFeedback(e.target.value)}
            placeholder="e.g., Make the hook more aggressive, shorten the body copy, add more urgency..."
            className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-2 text-white text-sm placeholder-slate-500 resize-none"
            rows={2}
            data-testid="scripts-feedback-input"
          />
          <div className="flex justify-end items-center gap-2 mt-2">
            <select
              value={scriptLength}
              onChange={(e) => setScriptLength(e.target.value)}
              data-testid="script-length-select-regen"
              className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm"
            >
              <option value="15-30">15-30 sec</option>
              <option value="30-45">30-45 sec</option>
              <option value="45-60">45-60 sec</option>
            </select>
            <button
              onClick={generateScripts}
              disabled={generating}
              className="px-4 py-2 bg-slate-700 text-slate-300 rounded-lg hover:bg-slate-600 disabled:opacity-50 text-sm"
              data-testid="generate-more-scripts-btn"
            >
              {generating ? 'Generating...' : feedback ? 'Regenerate with feedback' : 'Generate more'}
            </button>
          </div>
        </div>
      )}

      {scriptConfirmFailures.length > 0 && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 mt-4"
             role="alert" data-testid="script-confirm-failures">
          <p className="text-red-300 text-sm">
            {scriptConfirmFailures.length} script(s) could not be confirmed, so
            this step did not move on:
          </p>
          <ul className="text-red-200 text-sm mt-1 space-y-1">
            {scriptConfirmFailures.map((f) => (
              <li key={f.label}>• <strong>{f.label}</strong> — {f.detail}</li>
            ))}
          </ul>
        </div>
      )}

      {scripts.length > 0 && (
        <div className="flex justify-end pt-4">
          <button
            onClick={async () => {
              const res = await S.confirmAll();
              setScriptConfirmFailures(res.failures);
              // A partial result does not advance: an unconfirmed script would
              // be named `B1` in production, silently.
              if (res.clean) onComplete();
            }}
            disabled={S.busyScript === '__all__'}
            className="px-6 py-2 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 disabled:opacity-50"
            data-testid="continue-to-cta-btn"
          >
            {S.busyScript === '__all__' ? 'Confirming…' : 'Confirm & continue'}
          </button>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// CTA Step (I1) — split out of Script Generation.
//
// WHERE AFFILIATE CTAs ACTUALLY LIVE (corrected 2026-09-03, CREATIVE-STEP-HANDOFF).
// This header used to say "Scripts already carry cta_variants generated
// alongside the script body". That is FALSE for affiliate scripts and always
// was: `cta_variants` exists only on the ABH path (`abh_chat_locking.py`,
// `abh_engine.py`) and in a request model. Affiliate CTAs are `copy_elements`
// rows of type CTA carrying the `script_id` they belong to — three per script
// on a normal generation — and `scripts.cta_element_ids` is null on every row.
// Reading `s.cta_variants` counted zero no matter how many CTAs existed, which
// is what put "0 CTA variants across 0 scripts" on screen while 66 CTAs sat in
// the database.
//
// Custom CTAs are still authored per script via POST /scripts/{id}/cta/custom.
// ============================================================================
export const CTAStep = ({ offer, onComplete, sessionId, isNewSession, sessionStartTime, refreshKey = 0 }) => {
  const [scripts, setScripts] = useState([]);
  const [ctaElements, setCtaElements] = useState([]);
  // Three states, not two. `loading` alone cannot tell "never fetched" from
  // "fetched and found nothing" from "the fetch failed".
  const [loadState, setLoadState] = useState('loading'); // loading | error | ok
  const [loading, setLoading] = useState(true);
  const [hiddenScripts, setHiddenScripts] = useState({ hiddenOtherSession: 0, hiddenUnstamped: 0 });
  const [customCtaFor, setCustomCtaFor] = useState(null); // script_id or null
  const [customCtaText, setCustomCtaText] = useState('');
  const [saving, setSaving] = useState(false);
  // ITEM 6 — this step used to only READ the three CTAs that script generation
  // produced as a by-product. It now generates its own list and records which
  // one is chosen: `selectedByScript` mirrors `scripts.cta_element_id`, the
  // field whose absence put a hard-coded "C1" into every production's permanent
  // name while the script's real CTAs were C37/C38/C39.
  const [generatingFor, setGeneratingFor] = useState(null);   // script_id
  const [selectingFor, setSelectingFor] = useState(null);     // element_id
  const [selectedByScript, setSelectedByScript] = useState({});
  const [stepError, setStepError] = useState(null);
  const [feedbackFor, setFeedbackFor] = useState(null);       // script_id
  const [feedbackText, setFeedbackText] = useState('');

  // ALWAYS FETCH, THEN SCOPE — the pattern PromptsStep already uses.
  // This step used to skip the fetch entirely when `isNewSession` was true. That
  // guard is right for a step that GENERATES — it repopulates from its own POST
  // response and must not show stale work — and wrong for a step that only READS
  // what earlier steps produced: with nothing of its own to generate, it could
  // never populate and rendered empty for the whole session.
  const fetchScripts = useCallback(async () => {
    setLoading(true);
    try {
      const [scriptRes, ctaRes] = await Promise.all([
        axios.get(`${API}/offers/${offer.offer_id}/scripts`),
        axios.get(`${API}/copy-elements`, {
          params: { offer_id: offer.offer_id, element_type: 'CTA', limit: 200 },
        }),
      ]);
      // ITEM 7 — scope on the session the rows name, not on a timestamp that
      // only exists for a brand-new session.
      const opts = { isNewSession, sessionStartTime };
      const scopedScripts = scopeToSession(scriptRes.data.scripts, sessionId, opts);
      const rows = scopedScripts.rows;
      const ctas = scopeToSession(ctaRes.data.elements, sessionId, opts).rows;
      setScripts(rows);
      setCtaElements(ctas);
      setHiddenScripts({
        hiddenOtherSession: scopedScripts.hiddenOtherSession,
        hiddenUnstamped: scopedScripts.hiddenUnstamped,
      });
      // Which CTA each script has actually committed to.
      setSelectedByScript(
        rows.reduce((acc, r) => {
          if (r.cta_element_id) acc[r.script_id] = r.cta_element_id;
          return acc;
        }, {})
      );
      setLoadState('ok');
    } catch (error) {
      console.error('Failed to fetch scripts for CTA stage:', error);
      setLoadState('error');
    } finally {
      setLoading(false);
    }
  }, [offer.offer_id, sessionId, isNewSession, sessionStartTime]);

  useEffect(() => { fetchScripts(); }, [fetchScripts, refreshKey]);

  // Generate a fresh batch of candidates for one script. Errors are shown on
  // the step, named, rather than thrown into an alert the operator dismisses.
  const generateCtas = async (scriptId, feedback) => {
    setGeneratingFor(scriptId);
    setStepError(null);
    try {
      await axios.post(`${API}/scripts/${scriptId}/ctas/generate`, {
        count: 6,
        feedback: feedback || undefined,
      });
      await fetchScripts();
      setFeedbackFor(null);
      setFeedbackText('');
    } catch (error) {
      const detail = error.response?.data?.detail || error.message || 'no reason given';
      console.error('CTA generation failed:', error);
      setStepError(`Could not generate CTAs for ${scriptId}: ${detail}`);
    } finally {
      setGeneratingFor(null);
    }
  };

  // THE point of this step. Writes `cta_element_id` onto the script, which is
  // what finally lets the production name and both brief builders say which CTA
  // is being built.
  const selectCta = async (scriptId, elementId) => {
    setSelectingFor(elementId);
    setStepError(null);
    try {
      const res = await axios.post(`${API}/scripts/${scriptId}/cta/select`, {
        element_id: elementId,
      });
      setSelectedByScript(prev => ({ ...prev, [scriptId]: elementId }));
      setScripts(prev => prev.map(sc => sc.script_id === scriptId
        ? { ...sc, cta_element_id: elementId, cta_element_number: res.data?.cta_element_number }
        : sc));
      await fetchScripts();
    } catch (error) {
      const detail = error.response?.data?.detail || error.message || 'no reason given';
      console.error('CTA selection failed:', error);
      setStepError(`Could not select that CTA: ${detail}`);
    } finally {
      setSelectingFor(null);
    }
  };

  const addCustomCta = async (scriptId) => {
    if (!customCtaText.trim()) return;
    setSaving(true);
    try {
      const response = await axios.post(`${API}/scripts/${scriptId}/cta/custom`, {
        content: customCtaText.trim(),
      });
      if (response.data?.status !== 'error') {
        await fetchScripts();
      }
      setCustomCtaFor(null);
      setCustomCtaText('');
    } catch (error) {
      console.error('Failed to add custom CTA:', error);
      alert(error.response?.data?.detail || 'Failed to add CTA');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-emerald-500"></div>
      </div>
    );
  }

  // Grouped from the CTA elements themselves, by the script_id each carries.
  const ctasByScript = ctaElements.reduce((acc, el) => {
    if (!el.script_id) return acc;
    (acc[el.script_id] = acc[el.script_id] || []).push(el);
    return acc;
  }, {});
  const totalCtas = ctaElements.filter(e => e.script_id).length;

  return (
    <div className="space-y-6" data-testid="cta-step">
      <div>
        <h3 className="text-lg font-semibold text-white">CTA</h3>
        <p className="text-slate-400 text-sm">
          Generate CTAs for each script and <strong className="text-white">pick the one it will be built with</strong>.
          The chosen CTA goes into the creative's permanent name and is marked
          "USE THIS ONE" on the editor's brief.
        </p>
      </div>

      {/* Errors named on the step, not thrown into an alert and lost. */}
      {stepError && (
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg px-4 py-3" data-testid="cta-step-error">
          <p className="text-amber-400 text-sm">{stepError}</p>
        </div>
      )}

      {/* RULE 10: three causes, three answers. These were one message, which is
          why a loading fault read as an empty library and cost an evening. */}
      {loadState === 'error' ? (
        <div className="text-center py-12 bg-slate-900/30 rounded-xl border border-amber-800/50" data-testid="cta-load-failed">
          <p className="text-amber-400">Could not load scripts or CTAs</p>
          <p className="text-slate-500 text-sm mt-1">
            The request failed. This is not the same as having none — reload the
            step, and if it keeps failing the scripts API is unavailable.
          </p>
        </div>
      ) : scripts.length === 0 ? (
        <div className="text-center py-12 bg-slate-900/30 rounded-xl border border-slate-800" data-testid="cta-genuinely-empty">
          <p className="text-slate-400">No scripts in this session yet</p>
          <p className="text-slate-500 text-sm mt-1">
            Loaded successfully and found none. Generate scripts in the Script
            stage and they will appear here.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {scripts.map(script => (
            <div key={script.script_id} className="bg-slate-900/50 border border-slate-800 rounded-xl p-4" data-testid={`cta-script-card-${script.script_id}`}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <span className="text-emerald-400 font-mono text-sm">{script.body_element_number || script.script_id?.slice(0, 10)}</span>
                  <span className="text-slate-400 text-sm">{script.platform} &bull; {script.total_duration_seconds}s</span>
                </div>
                <button
                  onClick={() => { setCustomCtaFor(customCtaFor === script.script_id ? null : script.script_id); setCustomCtaText(''); }}
                  className="px-3 py-1 text-xs bg-slate-700 text-slate-300 rounded hover:bg-slate-600"
                  data-testid={`add-custom-cta-toggle-${script.script_id}`}
                >
                  {customCtaFor === script.script_id ? 'Cancel' : '+ Custom CTA'}
                </button>
              </div>

              {(ctasByScript[script.script_id] || []).length > 0 ? (
                <div className="space-y-1.5" data-testid={`cta-list-${script.script_id}`}>
                  {(ctasByScript[script.script_id] || []).map((cta, i) => {
                    const chosen = selectedByScript[script.script_id] === cta.element_id;
                    const locked = !!script.production_id;
                    return (
                      <div
                        key={cta.element_id || i}
                        className={`flex items-start gap-3 rounded-lg px-3 py-2 border ${
                          chosen
                            ? 'bg-emerald-500/10 border-emerald-500/40'
                            : 'bg-slate-800/50 border-transparent'
                        }`}
                        data-testid={`cta-option-${cta.element_id}`}
                      >
                        <span className="text-slate-500 text-xs font-mono mt-0.5 shrink-0">
                          {cta.element_number || '—'}
                        </span>
                        <div className="flex-1 min-w-0">
                          <p className="text-white text-sm">{cta.content}</p>
                          {cta.cta_type && (
                            <span className="text-slate-500 text-[11px]">{cta.cta_type}</span>
                          )}
                          {cta.rationale && (
                            <p className="text-slate-500 text-[11px] mt-0.5">{cta.rationale}</p>
                          )}
                        </div>
                        {chosen ? (
                          <span
                            className="px-2 py-0.5 text-[11px] rounded bg-emerald-500/20 text-emerald-400 shrink-0"
                            data-testid={`cta-chosen-${cta.element_id}`}
                          >
                            Using this
                          </span>
                        ) : (
                          <button
                            onClick={() => selectCta(script.script_id, cta.element_id)}
                            disabled={selectingFor === cta.element_id || locked}
                            title={locked
                              ? 'This script has already been routed to production; its name already carries a CTA number.'
                              : 'Build this script with this CTA'}
                            className="px-2.5 py-1 text-[11px] bg-slate-700 text-slate-300 rounded hover:bg-slate-600 disabled:opacity-40 shrink-0"
                            data-testid={`cta-select-${cta.element_id}`}
                          >
                            {selectingFor === cta.element_id ? '…' : 'Use this'}
                          </button>
                        )}
                      </div>
                    );
                  })}
                  {/* Rule 10 on the screen: having CTAs and having CHOSEN one
                      are different states, and the second is what production
                      actually needs. */}
                  {!selectedByScript[script.script_id] && (
                    <p className="text-amber-400 text-xs pt-1" data-testid={`cta-none-selected-${script.script_id}`}>
                      No CTA chosen yet. Until one is, this script's creative is
                      named with a placeholder CTA number and the brief cannot
                      tell the editor which line to use.
                    </p>
                  )}
                </div>
              ) : (
                <p className="text-slate-500 text-sm" data-testid={`cta-empty-${script.script_id}`}>
                  No CTAs on this script yet. Generate some below.
                </p>
              )}

              {/* Generate a real list — the thing this step did not have. */}
              <div className="flex items-center gap-2 mt-3 pt-3 border-t border-slate-800">
                <button
                  onClick={() => generateCtas(script.script_id)}
                  disabled={generatingFor === script.script_id}
                  className="px-3 py-1.5 text-xs bg-emerald-500 text-white rounded hover:bg-emerald-600 disabled:opacity-50"
                  data-testid={`generate-ctas-${script.script_id}`}
                >
                  {generatingFor === script.script_id
                    ? 'Generating…'
                    : (ctasByScript[script.script_id] || []).length > 0
                      ? 'Generate more'
                      : 'Generate CTAs'}
                </button>
                <button
                  onClick={() => {
                    setFeedbackFor(feedbackFor === script.script_id ? null : script.script_id);
                    setFeedbackText('');
                  }}
                  className="px-3 py-1.5 text-xs bg-slate-700 text-slate-300 rounded hover:bg-slate-600"
                  data-testid={`cta-feedback-toggle-${script.script_id}`}
                >
                  {feedbackFor === script.script_id ? 'Cancel' : 'Generate with notes'}
                </button>
              </div>

              {feedbackFor === script.script_id && (
                <div className="mt-2">
                  <textarea
                    value={feedbackText}
                    onChange={(e) => setFeedbackText(e.target.value)}
                    placeholder="What was wrong with the last batch? e.g. too pushy, no urgency that is actually true..."
                    rows={2}
                    className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm placeholder-slate-500 resize-none"
                    data-testid={`cta-feedback-input-${script.script_id}`}
                  />
                  <div className="flex justify-end mt-2">
                    <button
                      onClick={() => generateCtas(script.script_id, feedbackText)}
                      disabled={generatingFor === script.script_id || feedbackText.trim().length < 3}
                      className="px-3 py-1.5 text-xs bg-emerald-500 text-white rounded hover:bg-emerald-600 disabled:opacity-50"
                      data-testid={`cta-feedback-submit-${script.script_id}`}
                    >
                      {generatingFor === script.script_id ? 'Generating…' : 'Generate with these notes'}
                    </button>
                  </div>
                </div>
              )}

              {customCtaFor === script.script_id && (
                <div className="mt-3 pt-3 border-t border-slate-800">
                  <textarea
                    value={customCtaText}
                    onChange={(e) => setCustomCtaText(e.target.value)}
                    placeholder="e.g., Claim your discount before midnight..."
                    rows={2}
                    className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm placeholder-slate-500 resize-none"
                    data-testid={`custom-cta-input-${script.script_id}`}
                  />
                  <div className="flex justify-end mt-2">
                    <button
                      onClick={() => addCustomCta(script.script_id)}
                      disabled={saving || customCtaText.trim().length < 3}
                      className="px-3 py-1.5 text-xs bg-emerald-500 text-white rounded hover:bg-emerald-600 disabled:opacity-50"
                      data-testid={`custom-cta-submit-${script.script_id}`}
                    >
                      {saving ? 'Saving…' : 'Add CTA'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <div className="flex items-center justify-between pt-4 border-t border-slate-800">
        <div>
          <p className="text-sm text-slate-400">
            {Object.keys(selectedByScript).length} of {scripts.length} script{scripts.length !== 1 ? 's' : ''} have a CTA chosen
            <span className="text-slate-500"> &middot; {totalCtas} CTA{totalCtas !== 1 ? 's' : ''} generated</span>
          </p>
          <HiddenRowsNote {...hiddenScripts} testid="cta-hidden-scripts" />
        </div>
        <button
          onClick={onComplete}
          className="px-6 py-2 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600"
          data-testid="continue-to-elements-btn"
        >
          Continue to Creative Elements
        </button>
      </div>
    </div>
  );
};

// Production Step
// ============================================================================
// Prompts Step (I1) — renamed from the old "Production" step. Creative-type
// selection and submit-to-production moved to CreativeElementsStep; this
// stage is the Veo prompt / production monitoring surface: productions list,
// prompt-component quality scoring, delivery audit, Drive/Trello links.
// ============================================================================
export const PromptsStep = ({ offer, sessionId, isNewSession, sessionStartTime, onFinished }) => {
  // ITEM 11 — this step is the last one and had NO terminal action of any kind:
  // no onComplete, "Next" disabled, nothing to press. Meanwhile
  // `POST /creative/sessions/{id}/complete` has existed since the session model
  // was built and nothing has ever called it. Live, 2026-09-08: all four
  // sessions for HOMESE015 were still `active`, one since 2026-07-19 with every
  // script routed.
  const [finishing, setFinishing] = useState(false);
  const [finishResult, setFinishResult] = useState(null);  // the endpoint's own report
  const [finishError, setFinishError] = useState(null);
  const [productions, setProductions] = useState([]);
  const [deliveryAudit, setDeliveryAudit] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadingDrive, setUploadingDrive] = useState(null);
  const [retryingAudit, setRetryingAudit] = useState(null);
  const [scoringProd, setScoringProd] = useState(null);
  // Veo component scoring (per production)
  const [scorePanelOpen, setScorePanelOpen] = useState(null);   // production_id
  const [scoreDraft, setScoreDraft] = useState({});             // { [production_id]: { overall, overall_notes, components: {cinematography:{score,notes}, ...} } }
  const VEO_COMPONENT_KEYS = ['cinematography', 'subject', 'action', 'context', 'style_ambiance'];
  const VEO_COMPONENT_LABELS = {
    cinematography: 'Cinematography',
    subject: 'Subject',
    action: 'Action',
    context: 'Context',
    style_ambiance: 'Style & Ambiance',
  };

  const finishSession = async () => {
    if (!sessionId) return;
    setFinishing(true);
    setFinishError(null);
    try {
      const res = await axios.post(`${API}/creative/sessions/${sessionId}/complete`);
      setFinishResult(res.data);
      if (onFinished) onFinished(res.data);
    } catch (error) {
      // Named, on the step. A finish that quietly failed would leave the
      // session `active` and look exactly like a finish that worked — which is
      // how four sessions stayed open for two months.
      const detail = error.response?.data?.detail || error.message || 'no reason given';
      console.error('Failed to complete session:', error);
      setFinishError(`Could not finish this session: ${detail}`);
    } finally {
      setFinishing(false);
    }
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const prodRes = await axios.get(`${API}/offers/${offer.offer_id}/productions`);
      let prods = prodRes.data.productions || [];

      // For new sessions, filter to only items created in THIS session
      if (isNewSession && sessionStartTime) {
        const cutoff = new Date(sessionStartTime);
        prods = prods.filter(p => p.created_at && new Date(p.created_at) >= cutoff);
      }

      setProductions(prods);

      if (prods.length > 0) {
        try {
          const auditPromises = prods.map(p =>
            axios.get(`${API}/delivery-audit?production_id=${p.production_id}`).catch(() => ({ data: { audit_entries: [] } }))
          );
          const auditResults = await Promise.all(auditPromises);
          setDeliveryAudit(auditResults.flatMap(r => r.data?.audit_entries || []));
        } catch (e) {
          console.debug('Delivery audit fetch failed:', e);
        }
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  }, [offer.offer_id, isNewSession, sessionStartTime]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // REMOVED 2026-09-02 (CREATIVE-AI-VIDEO-RELABEL): the 15s auto-refresh that
  // watched for active Veo jobs to finish. Nothing submits a Veo job any more,
  // so it could only ever have polled for a state that can no longer be entered.

  // Creative-type + submit-to-production moved to CreativeElementsStep (I1) —
  // same PATCH /scripts/{id}/creative-type and POST /scripts/{id}/route-to-production
  // endpoints, called from there.

  const uploadToDrive = async (productionId) => {
    setUploadingDrive(productionId);
    try {
      const response = await axios.post(`${API}/productions/${productionId}/upload-to-drive`);
      if (response.data.status === 'success') {
        alert(`Uploaded to Google Drive!\nPath: ${response.data.folder_path}\nMode: ${response.data.mode}`);
      } else {
        alert(response.data.message || 'Upload completed');
      }
      fetchData();
    } catch (error) {
      console.error('Failed to upload to Drive:', error);
      alert(error.response?.data?.detail || 'Failed to upload to Drive');
    } finally {
      setUploadingDrive(null);
    }
  };

  // createCampaignCreative removed — creatives are now registered automatically
  // when the production brief is generated, under the same canonical name the
  // brief prints (see production_routing._register_creative_for_production).

  // assignToCampaign REMOVED 2026-09-02 (CAMPAIGNS-REMOVAL-EXECUTE) with the
  // Campaign Assignment row that was its only caller. It was the only writer of
  // `campaigns.active_creative_id` in the whole system.

  const downloadBrief = (productionId) => {
    window.open(`${API}/productions/${productionId}/brief`, '_blank');
  };

  // triggerVeoPoll REMOVED 2026-09-02 (CREATIVE-AI-VIDEO-RELABEL) with the
  // "Check Veo Status" button that was its only caller. Veo is retired.

  const retryDelivery = async (auditId) => {
    setRetryingAudit(auditId);
    try {
      await axios.post(`${API}/delivery-audit/${auditId}/retry`);
      fetchData();
    } catch (error) {
      console.error('Retry failed:', error);
      alert(error.response?.data?.detail || 'Retry failed');
    } finally {
      setRetryingAudit(null);
    }
  };

  // ── Veo component scoring ──────────────────────────────────────────────
  const openScorePanel = (prod) => {
    if (scorePanelOpen === prod.production_id) {
      setScorePanelOpen(null);
      return;
    }
    const existingComp = prod.veo_quality_components || {};
    setScoreDraft(prev => ({
      ...prev,
      [prod.production_id]: {
        overall: prod.veo_quality_score || 0,
        overall_notes: prod.veo_quality_notes || '',
        components: VEO_COMPONENT_KEYS.reduce((acc, k) => {
          const v = existingComp[k] || {};
          acc[k] = { score: v.score || 0, notes: v.notes || '' };
          return acc;
        }, {}),
      },
    }));
    setScorePanelOpen(prod.production_id);
  };

  const setDraftOverall = (pid, val) => {
    setScoreDraft(prev => ({
      ...prev,
      [pid]: { ...(prev[pid] || {}), overall: val },
    }));
  };

  const setDraftOverallNotes = (pid, val) => {
    setScoreDraft(prev => ({
      ...prev,
      [pid]: { ...(prev[pid] || {}), overall_notes: val },
    }));
  };

  const setDraftCompScore = (pid, compKey, val) => {
    setScoreDraft(prev => ({
      ...prev,
      [pid]: {
        ...(prev[pid] || {}),
        components: {
          ...((prev[pid] || {}).components || {}),
          [compKey]: {
            ...(((prev[pid] || {}).components || {})[compKey] || {}),
            score: val,
          },
        },
      },
    }));
  };

  const setDraftCompNotes = (pid, compKey, val) => {
    setScoreDraft(prev => ({
      ...prev,
      [pid]: {
        ...(prev[pid] || {}),
        components: {
          ...((prev[pid] || {}).components || {}),
          [compKey]: {
            ...(((prev[pid] || {}).components || {})[compKey] || {}),
            notes: val,
          },
        },
      },
    }));
  };

  const submitComponentScore = async (productionId) => {
    const draft = scoreDraft[productionId] || {};
    if (!draft.overall || draft.overall < 1 || draft.overall > 5) return;
    if (!(draft.overall_notes || '').trim()) return;

    setScoringProd(productionId);
    try {
      const body = {
        score: draft.overall,
        notes: draft.overall_notes.trim(),
        components: {},
      };
      VEO_COMPONENT_KEYS.forEach(k => {
        const c = (draft.components || {})[k] || {};
        if (c.score || (c.notes || '').trim()) {
          body.components[k] = {
            score: c.score || null,
            notes: (c.notes || '').trim(),
          };
        }
      });
      const response = await axios.post(
        `${API}/productions/${productionId}/veo-quality-score`,
        body
      );
      setProductions(prev => prev.map(p =>
        p.production_id === productionId
          ? {
              ...p,
              veo_quality_score: response.data.overall_score,
              veo_quality_components: response.data.components,
              veo_quality_weak_components: response.data.weak_components || [],
              veo_quality_notes: body.notes,
            }
          : p
      ));
      setScorePanelOpen(null);
    } catch (error) {
      console.error('Failed to submit quality score:', error);
      alert(error.response?.data?.detail || 'Failed to submit score');
    } finally {
      setScoringProd(null);
    }
  };

  // Helper to get delivery audit entries for a specific production
  const getProductionAudit = (productionId) =>
    deliveryAudit.filter(a => a.production_id === productionId);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-emerald-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="production-step">
      <div>
        <h3 className="text-lg font-semibold text-white">Prompts</h3>
        <p className="text-slate-400 text-sm">Production status, Veo prompt review, delivery and quality scoring</p>
      </div>

      {/* Productions */}
      {productions.length > 0 ? (
        <div className="space-y-3">
          <h4 className="text-white font-medium">Productions ({productions.length})</h4>
          {productions.map(prod => {
            const hasCreativeId = prod.creative_id;
            return (
              <div 
                key={prod.production_id}
                className="bg-slate-900/50 border border-slate-800 rounded-xl p-4"
                data-testid={`production-card-${prod.production_id}`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className={`px-2 py-1 text-xs rounded font-medium ${
                      prod.creative_type === 'AI' ? 'bg-purple-500/20 text-purple-400' :
                      prod.creative_type === 'VE' ? 'bg-blue-500/20 text-blue-400' :
                      prod.creative_type === 'UGC' ? 'bg-pink-500/20 text-pink-400' :
                      'bg-slate-500/20 text-slate-400'
                    }`}>
                      {prod.creative_type === 'AI' ? 'AI Video' : prod.creative_type}
                    </span>
                    <span className={`px-2 py-1 text-xs rounded ${
                      prod.status === 'BRIEF_GENERATED' ? 'bg-emerald-500/20 text-emerald-400' :
                      prod.status === 'IN_PRODUCTION' ? 'bg-blue-500/20 text-blue-400' :
                      prod.status === 'COMPLETED' ? 'bg-emerald-500/20 text-emerald-400' :
                      prod.status === 'FAILED' ? 'bg-red-500/20 text-red-400' :
                      prod.status === 'VEO_SUBMITTED' ? 'bg-yellow-500/20 text-yellow-400' :
                      prod.status === 'VEO_PROCESSING' ? 'bg-blue-500/20 text-blue-400' :
                      prod.status === 'SUBMITTING' ? 'bg-yellow-500/20 text-yellow-400' :
                      prod.status === 'CLIPS_READY' ? 'bg-orange-500/20 text-orange-400' :
                      'bg-slate-500/20 text-slate-400'
                    }`}>
                      {prod.status === 'CLIPS_READY' ? 'AWAITING ASSEMBLY' : prod.status?.replace(/_/g, ' ')}
                    </span>
                    {prod.ai_tool === 'veo-3.1' && prod.veo_status && (
                      <span className={`px-2 py-1 text-xs rounded font-mono ${
                        prod.veo_status === 'complete' || prod.veo_status === 'clips_uploaded' ? 'bg-emerald-500/15 text-emerald-400' :
                        prod.veo_status === 'processing' || prod.veo_status === 'submitted' || prod.veo_status === 'multi_clip_generating' || prod.veo_status === 'multi_clip_polling' ? 'bg-blue-500/15 text-blue-400 animate-pulse' :
                        prod.veo_status === 'api_key_missing' || prod.veo_status === 'submission_failed' || prod.veo_status === 'timeout' || prod.veo_status === 'poll_failed' || prod.veo_status === 'download_failed' || prod.veo_status === 'stitch_failed' || prod.veo_status === 'all_clips_failed' ? 'bg-red-500/15 text-red-400' :
                        'bg-slate-500/15 text-slate-400'
                      }`} data-testid={`veo-status-${prod.production_id}`}>
                        Veo: {prod.veo_status?.replace(/_/g, ' ')}
                        {prod.multi_clip && prod.clips_total > 0 && ` (${prod.clips_completed || 0}/${prod.clips_total} clips)`}
                      </span>
                    )}
                    {prod.drive_uploaded && (
                      <span className="px-2 py-1 text-xs bg-cyan-500/10 text-cyan-400 rounded">On Drive</span>
                    )}
                    {prod.creative_identifier && (
                      <span className="px-2 py-1 text-xs bg-indigo-500/15 text-indigo-300 rounded font-mono" data-testid={`creative-id-${prod.production_id}`}>
                        {prod.creative_identifier}
                      </span>
                    )}
                  </div>
                  <div className="flex gap-2 flex-wrap justify-end">
                    {prod.brief_filename && (
                      <button
                        onClick={() => downloadBrief(prod.production_id)}
                        data-testid={`download-brief-btn-${prod.production_id}`}
                        className="px-3 py-1 text-sm bg-slate-700 text-white rounded hover:bg-slate-600"
                      >
                        Download Brief
                      </button>
                    )}
                    {prod.brief_filename && !prod.drive_uploaded && (
                      <button
                        onClick={() => uploadToDrive(prod.production_id)}
                        disabled={uploadingDrive === prod.production_id}
                        data-testid={`upload-drive-btn-${prod.production_id}`}
                        className="px-3 py-1 text-sm bg-cyan-500/10 text-cyan-400 rounded hover:bg-cyan-500/20 disabled:opacity-50"
                      >
                        {uploadingDrive === prod.production_id ? 'Uploading...' : 'Upload to Drive'}
                      </button>
                    )}
                    {(prod.google_drive_url || prod.drive_web_link) && (
                      <a
                        href={prod.google_drive_url || prod.drive_web_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`open-drive-btn-${prod.production_id}`}
                        className="px-3 py-1 text-sm bg-blue-500/10 text-blue-400 rounded hover:bg-blue-500/20"
                      >
                        Open in Drive
                      </a>
                    )}
                    {/* REMOVED 2026-09-02, CREATIVE-AI-VIDEO-RELABEL — the two Veo
                        platform controls that used to sit here: "Check Veo Status"
                        (POST /productions/{id}/veo-poll) and "Multi-Clip Generate"
                        (POST /productions/{id}/veo-multi-clip). Veo is retired.

                        Multi-Clip Generate was also the dead end recorded in
                        docs/audit/CREATIVE_REACHABILITY.md: it returned 409 telling
                        the operator to call an approval endpoint by hand, because
                        the only screen that could approve was unreachable. Removing
                        the button removes the dead end. AI Video now reaches the
                        video editor through the brief, which is what actually
                        happens today. */}
                  </div>
                </div>

                {/* The Campaign Assignment row was removed 2026-09-02
                    (CAMPAIGNS-REMOVAL-EXECUTE). It was the ONLY writer of
                    `campaigns.active_creative_id` anywhere in Cipher, and it
                    wrote to campaigns that were never registered. Creative
                    identity now comes from the ad name in the nightly platform
                    feed. See docs/audit/TOMBSTONE_CAMPAIGNS.md. */}

                <p className="text-slate-500 text-xs mt-2">
                  Production ID: {prod.production_id}
                  {prod.drive_folder_path && <span> &bull; Drive: {prod.drive_folder_path}</span>}
                  {prod.veo_video_size_mb && <span> &bull; Video: {prod.veo_video_size_mb} MB</span>}
                </p>

                {/* Prominent Drive Link */}
                {(prod.google_drive_url || prod.drive_web_link) && (
                  <a
                    href={prod.google_drive_url || prod.drive_web_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 mt-2 px-3 py-1.5 bg-cyan-500/10 border border-cyan-500/20 rounded-lg text-cyan-400 text-xs hover:bg-cyan-500/20 transition-colors w-fit"
                    data-testid={`drive-link-prominent-${prod.production_id}`}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                    Google Drive — {prod.drive_folder_path || 'View File'}
                  </a>
                )}

                {prod.veo_error && (
                  <div className="mt-2 bg-red-500/5 border border-red-500/20 rounded p-2" data-testid={`veo-error-${prod.production_id}`}>
                    <p className="text-red-400 text-xs font-medium">Veo Error:</p>
                    <p className="text-red-300 text-xs">{prod.veo_error}</p>
                  </div>
                )}

                {/* Multi-clip Assembly Status */}
                {prod.multi_clip && prod.status === 'CLIPS_READY' && (
                  <div className="mt-3 bg-orange-500/5 border border-orange-500/20 rounded-lg p-3" data-testid={`clips-ready-section-${prod.production_id}`}>
                    <div className="flex items-center gap-2 mb-2">
                      <svg className="w-4 h-4 text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 4V2m0 2a2 2 0 012 2v1h2V6a2 2 0 012-2m0 0V2m0 2a2 2 0 012 2v1h2a2 2 0 012 2v10a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h2V6a2 2 0 012-2" />
                      </svg>
                      <span className="text-orange-400 text-sm font-medium">
                        {prod.clips_completed || 0} of {prod.clips_total || 0} clips ready for assembly
                      </span>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      {(prod.google_drive_url || prod.drive_web_link) && (
                        <a
                          href={prod.google_drive_url || prod.drive_web_link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-cyan-500/10 border border-cyan-500/20 rounded text-cyan-400 text-xs hover:bg-cyan-500/20 transition-colors"
                          data-testid={`clips-drive-link-${prod.production_id}`}
                        >
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                          </svg>
                          AI Video Folder in Drive
                        </a>
                      )}
                      {prod.trello_card_url && (
                        <a
                          href={prod.trello_card_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-blue-500/10 border border-blue-500/20 rounded text-blue-400 text-xs hover:bg-blue-500/20 transition-colors"
                          data-testid={`clips-trello-link-${prod.production_id}`}
                        >
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                          </svg>
                          Trello Assembly Card
                        </a>
                      )}
                    </div>
                  </div>
                )}

                {/* Video Quality Score — component-level */}
                {(prod.status === 'COMPLETED' || prod.status === 'CLIPS_READY') && prod.creative_type === 'AI' && (
                  <div className="mt-3 bg-slate-800/30 border border-slate-700/50 rounded-lg p-3" data-testid={`quality-score-section-${prod.production_id}`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-slate-300 text-xs font-medium">Video Quality Score</p>
                        {prod.veo_quality_score ? (
                          <>
                            <span
                              className="px-2 py-0.5 text-xs rounded bg-amber-500/15 text-amber-300 font-medium"
                              data-testid={`quality-score-summary-${prod.production_id}`}
                            >
                              Overall {prod.veo_quality_score}/5
                            </span>
                            {(prod.veo_quality_weak_components || []).map(w => (
                              <span
                                key={w}
                                className="px-2 py-0.5 text-xs rounded bg-red-500/10 text-red-400 border border-red-500/30"
                                data-testid={`quality-weak-chip-${prod.production_id}-${w}`}
                              >
                                {VEO_COMPONENT_LABELS[w] || w}: {((prod.veo_quality_components || {})[w] || {}).score}
                              </span>
                            ))}
                          </>
                        ) : (
                          <span className="text-slate-500 text-xs">Not scored yet</span>
                        )}
                      </div>
                      <button
                        onClick={() => openScorePanel(prod)}
                        className="px-2 py-0.5 text-xs bg-slate-700 text-slate-300 rounded hover:bg-slate-600"
                        data-testid={`quality-score-toggle-${prod.production_id}`}
                      >
                        {scorePanelOpen === prod.production_id ? 'Cancel' : (prod.veo_quality_score ? 'Re-score' : 'Score video')}
                      </button>
                    </div>

                    {scorePanelOpen === prod.production_id && (() => {
                      const draft = scoreDraft[prod.production_id] || { overall: 0, overall_notes: '', components: {} };
                      const components = prod.veo_prompt_components || {};
                      const canSubmit = draft.overall >= 1 && draft.overall <= 5 && (draft.overall_notes || '').trim().length > 0;
                      return (
                        <div className="mt-3 space-y-4" data-testid={`quality-score-panel-${prod.production_id}`}>
                          {/* Overall */}
                          <div>
                            <label className="block text-xs text-slate-400 font-medium mb-1.5">Overall score</label>
                            <div className="flex items-center gap-1" data-testid={`overall-stars-${prod.production_id}`}>
                              {[1, 2, 3, 4, 5].map(star => (
                                <button
                                  key={star}
                                  onClick={() => setDraftOverall(prod.production_id, star)}
                                  className={`w-8 h-8 rounded transition-colors text-lg ${
                                    draft.overall >= star
                                      ? 'bg-amber-500/20 text-amber-400'
                                      : 'bg-slate-700/30 text-slate-600 hover:text-amber-400 hover:bg-amber-500/10'
                                  }`}
                                  data-testid={`overall-star-${prod.production_id}-${star}`}
                                >
                                  {draft.overall >= star ? '\u2605' : '\u2606'}
                                </button>
                              ))}
                              <span className="text-xs text-slate-500 ml-2">{draft.overall || 0}/5</span>
                            </div>
                            <textarea
                              value={draft.overall_notes || ''}
                              onChange={(e) => setDraftOverallNotes(prod.production_id, e.target.value)}
                              placeholder="What worked or didn't (required)"
                              rows={2}
                              className="mt-2 w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm placeholder-slate-500 resize-none"
                              data-testid={`overall-notes-${prod.production_id}`}
                            />
                          </div>

                          {/* Components */}
                          <div className="space-y-3">
                            <p className="text-xs text-slate-400 font-medium">Component scores (optional — each maps to the prompt piece below)</p>
                            {VEO_COMPONENT_KEYS.map(k => {
                              const compDraft = (draft.components || {})[k] || { score: 0, notes: '' };
                              const promptText = components[k] || '';
                              return (
                                <div key={k} className="bg-slate-900/40 border border-slate-800 rounded-lg p-3" data-testid={`component-row-${prod.production_id}-${k}`}>
                                  <div className="flex items-center justify-between mb-1.5">
                                    <span className="text-xs text-slate-200 font-medium">{VEO_COMPONENT_LABELS[k]}</span>
                                    <div className="flex items-center gap-1">
                                      {[1, 2, 3, 4, 5].map(star => (
                                        <button
                                          key={star}
                                          onClick={() => setDraftCompScore(prod.production_id, k, star === compDraft.score ? 0 : star)}
                                          className={`w-6 h-6 rounded text-sm ${
                                            compDraft.score >= star
                                              ? 'bg-amber-500/20 text-amber-400'
                                              : 'bg-slate-700/30 text-slate-600 hover:text-amber-400 hover:bg-amber-500/10'
                                          }`}
                                          data-testid={`component-star-${prod.production_id}-${k}-${star}`}
                                        >
                                          {compDraft.score >= star ? '\u2605' : '\u2606'}
                                        </button>
                                      ))}
                                    </div>
                                  </div>
                                  <p
                                    className="text-[11px] text-slate-500 leading-snug mb-1.5 font-mono bg-slate-950/60 rounded px-2 py-1.5"
                                    data-testid={`component-prompt-text-${prod.production_id}-${k}`}
                                  >
                                    {promptText || '(no prompt text captured for this component)'}
                                  </p>
                                  <input
                                    type="text"
                                    value={compDraft.notes || ''}
                                    onChange={(e) => setDraftCompNotes(prod.production_id, k, e.target.value)}
                                    placeholder="Notes for this component (optional)"
                                    className="w-full bg-slate-900 border border-slate-700 rounded px-2 py-1 text-white text-xs placeholder-slate-600"
                                    data-testid={`component-notes-${prod.production_id}-${k}`}
                                  />
                                </div>
                              );
                            })}
                          </div>

                          <div className="flex justify-end">
                            <button
                              onClick={() => submitComponentScore(prod.production_id)}
                              disabled={!canSubmit || scoringProd === prod.production_id}
                              className="px-4 py-1.5 text-xs bg-emerald-500 text-white rounded hover:bg-emerald-600 disabled:opacity-50"
                              data-testid={`submit-quality-score-${prod.production_id}`}
                            >
                              {scoringProd === prod.production_id ? 'Saving…' : 'Submit score'}
                            </button>
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                )}

                {/* Delivery Audit Indicators */}
                {getProductionAudit(prod.production_id).length > 0 && (
                  <div className="mt-2 space-y-1" data-testid={`delivery-audit-section-${prod.production_id}`}>
                    <p className="text-slate-500 text-xs font-medium">Delivery Status</p>
                    <div className="flex flex-wrap gap-1.5">
                      {getProductionAudit(prod.production_id).map(audit => (
                        <div
                          key={audit.audit_id}
                          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs ${
                            audit.status === 'SUCCESS' ? 'bg-emerald-500/10 text-emerald-400' :
                            audit.status === 'FAILED' ? 'bg-red-500/10 text-red-400' :
                            audit.status === 'RETRYING' ? 'bg-amber-500/10 text-amber-400' :
                            'bg-slate-500/10 text-slate-400'
                          }`}
                          data-testid={`delivery-badge-${audit.audit_id}`}
                        >
                          <span>{audit.action?.replace(/_/g, ' ')}</span>
                          <span className="font-medium">{audit.status}</span>
                          {audit.status === 'FAILED' && (
                            <button
                              onClick={() => retryDelivery(audit.audit_id)}
                              disabled={retryingAudit === audit.audit_id}
                              className="ml-1 px-1.5 py-0 bg-red-500/20 text-red-300 rounded hover:bg-red-500/30 disabled:opacity-50"
                              data-testid={`retry-btn-${audit.audit_id}`}
                            >
                              {retryingAudit === audit.audit_id ? '...' : 'Retry'}
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="text-center py-12 bg-slate-900/30 rounded-xl border border-slate-800">
          <p className="text-slate-400">No productions yet</p>
          <p className="text-slate-500 text-sm mt-1">
            Submit scripts to production from the Creative Elements stage
          </p>
        </div>
      )}

      {/* ── Finish. The last step finally has an end. ────────────────────── */}
      {sessionId && (
        <div className="pt-4 border-t border-slate-800" data-testid="finish-session-section">
          {finishError && (
            <p className="text-amber-400 text-sm mb-3" data-testid="finish-session-error">{finishError}</p>
          )}

          {finishResult ? (
            <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl px-5 py-4" data-testid="finish-session-done">
              <p className="text-emerald-400 font-medium">
                {finishResult.already_completed
                  ? 'This session was already finished.'
                  : 'Session finished.'}
              </p>
              {!finishResult.already_completed && (
                <p className="text-slate-400 text-sm mt-1">
                  {finishResult.scripts_routed} of {finishResult.scripts_in_session} script
                  {finishResult.scripts_in_session === 1 ? '' : 's'} in this session reached production.
                </p>
              )}
              {finishResult.unrouted_scripts > 0 && (
                // Said at the one moment it is still cheap to act on.
                <p className="text-amber-400 text-sm mt-1" data-testid="finish-unrouted-warning">
                  {finishResult.unrouted_scripts} script{finishResult.unrouted_scripts === 1 ? '' : 's'} were
                  never submitted and will not be produced.
                </p>
              )}
            </div>
          ) : (
            <div className="flex items-center justify-between bg-slate-900/50 border border-slate-800 rounded-xl px-5 py-4">
              <div>
                <p className="text-white font-medium">Finished with this session?</p>
                <p className="text-slate-500 text-xs mt-0.5">
                  Marks it complete so it stops appearing as active work. Nothing
                  is deleted, and everything above stays where it is.
                </p>
              </div>
              <button
                onClick={finishSession}
                disabled={finishing}
                className="px-6 py-2 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 disabled:opacity-50 font-medium shrink-0"
                data-testid="finish-session-btn"
              >
                {finishing ? 'Finishing…' : 'Finish Session'}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ============================================================================
// Creative Elements Step (I1 + I4) — set creative type per script, submit to
// production (same endpoints the old Production step used), and assemble
// modular hook+body+CTA combinations for testing multiple hooks against one
// script/CTA pairing via the existing `/offers/{id}/creatives/combine`
// endpoint (I4 — the copy_elements library already models hooks/bodies/CTAs
// as independent elements; this stage is the first UI to expose combining
// them many-to-one).
// ============================================================================
export const CreativeElementsStep = ({ offer, onComplete, sessionId, isNewSession, sessionStartTime, refreshKey = 0 }) => {
  // loading | error | ok — see the empty-state block. Distinguishing a
  // failed library read from an empty one is CLAUDE.md rule 10.
  const [libraryState, setLibraryState] = useState('loading');
  const [scripts, setScripts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [settingType, setSettingType] = useState(null);
  const [routingScript, setRoutingScript] = useState(null);
  const [routingAll, setRoutingAll] = useState(false);

  // I4 — modular assembly panel state
  const [hookElements, setHookElements] = useState([]);
  const [bodyElements, setBodyElements] = useState([]);
  const [ctaElements, setCtaElements] = useState([]);
  const [assemblyOpen, setAssemblyOpen] = useState(false);
  const [assemblyBody, setAssemblyBody] = useState('');
  const [assemblyCta, setAssemblyCta] = useState('');
  const [assemblyHooks, setAssemblyHooks] = useState([]); // multiple hook_element_ids
  const [assemblyChannel, setAssemblyChannel] = useState('META');
  const [assemblyType, setAssemblyType] = useState('AI');
  const [assembling, setAssembling] = useState(false);
  const [assembledCreatives, setAssembledCreatives] = useState([]);

  // ITEM 7. This fetched every script the OFFER had ever had and applied NO
  // filtering of any kind — the function directly below it always scoped, and
  // so did CTAStep. Live: 13 scripts from four sessions, all on screen, while
  // the operator's session had produced one.
  const [scriptsState, setScriptsState] = useState('loading'); // loading | error | ok
  const [hiddenScripts, setHiddenScripts] = useState({ hiddenOtherSession: 0, hiddenUnstamped: 0 });

  const fetchScripts = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/offers/${offer.offer_id}/scripts`);
      const scoped = scopeToSession(response.data.scripts, sessionId, {
        isNewSession, sessionStartTime,
      });
      setScripts(scoped.rows);
      setHiddenScripts({
        hiddenOtherSession: scoped.hiddenOtherSession,
        hiddenUnstamped: scoped.hiddenUnstamped,
      });
      setScriptsState('ok');
    } catch (error) {
      // Was console.error and nothing else: a failed read left the step looking
      // like a session with no scripts. Rule 10.
      console.error('Failed to fetch scripts for Creative Elements stage:', error);
      setScriptsState('error');
    } finally {
      setLoading(false);
    }
  }, [offer.offer_id, sessionId, isNewSession, sessionStartTime]);

  const fetchElementLibrary = useCallback(async () => {
    try {
      const [hooksRes, bodyRes, ctaRes] = await Promise.all([
        axios.get(`${API}/copy-elements`, { params: { offer_id: offer.offer_id, element_type: 'HOOK', limit: 100 } }),
        axios.get(`${API}/copy-elements`, { params: { offer_id: offer.offer_id, element_type: 'BODY', limit: 100 } }),
        axios.get(`${API}/copy-elements`, { params: { offer_id: offer.offer_id, element_type: 'CTA', limit: 100 } }),
      ]);
      // One scoping rule for the whole step — see `scopeToSession`. This used
      // to be a local timestamp filter that only worked for a NEW session.
      const scope = (rows) =>
        scopeToSession(rows, sessionId, { isNewSession, sessionStartTime }).rows;
      setHookElements(scope(hooksRes.data.elements));
      setBodyElements(scope(bodyRes.data.elements));
      setCtaElements(scope(ctaRes.data.elements));
      setLibraryState('ok');
    } catch (error) {
      // Was `console.debug` and nothing else — a failed read left the library
      // looking empty and said so only to the console. It is a fault, not an
      // absence, and the screen now says which.
      console.error('Copy element library read failed:', error);
      setLibraryState('error');
    }
  }, [offer.offer_id, sessionId, isNewSession, sessionStartTime]);

  // ALWAYS FETCH, THEN SCOPE — the PromptsStep pattern. See the note on
  // CTAStep's fetch: skipping the fetch for a new session is correct only for
  // a step that generates its own content. This one only reads.
  useEffect(() => {
    fetchScripts();
    fetchElementLibrary();
  }, [fetchScripts, fetchElementLibrary, refreshKey]);

  const unroutedScripts = scripts.filter(s => s.creative_type && !s.production_id);
  const needsType = scripts.filter(s => !s.creative_type && !s.production_id);

  // The select's value is "<creative_type>" for VE/UGC and
  // "AI:<destination>" for AI Video, because AI Video is one type with two
  // destinations beneath it. Splitting here keeps the wire format the backend
  // already understands: a creative_type plus an optional destination override.
  const setCreativeType = async (scriptId, value) => {
    const [type, destination] = String(value).split(':');
    setSettingType(scriptId);
    try {
      const body = { creative_type: type };
      if (destination) body.production_destination = destination;
      await axios.patch(`${API}/scripts/${scriptId}/creative-type`, body);
      setScripts(prev => prev.map(s => s.script_id === scriptId
        ? { ...s, creative_type: type, production_destination: destination || null }
        : s));
    } catch (error) {
      console.error('Failed to set creative type:', error);
      alert(error.response?.data?.detail || 'Failed to set creative type');
    } finally {
      setSettingType(null);
    }
  };

  const routeToProduction = async (scriptId, creativeType) => {
    setRoutingScript(scriptId);
    try {
      await axios.patch(`${API}/scripts/${scriptId}/creative-type`, { creative_type: creativeType });
      await axios.post(`${API}/scripts/${scriptId}/route-to-production`);
      fetchScripts();
    } catch (error) {
      console.error('Failed to route:', error);
      alert(error.response?.data?.detail || 'Failed to route to production');
    } finally {
      setRoutingScript(null);
    }
  };

  const submitAllToProduction = async () => {
    setRoutingAll(true);
    let routed = 0;
    const failures = [];
    try {
      for (const script of unroutedScripts) {
        const label = script.body_element_number || script.script_id?.slice(0, 10);
        try {
          await axios.patch(`${API}/scripts/${script.script_id}/creative-type`, {
            creative_type: script.creative_type,
          });
          await axios.post(`${API}/scripts/${script.script_id}/route-to-production`);
          routed++;
        } catch (err) {
          const detail = err.response?.data?.detail || err.response?.data?.error || err.message || 'Unknown error';
          failures.push({ label, detail, status: err.response?.status });
        }
      }
      if (routed > 0) fetchScripts();
      if (failures.length === 0) {
        alert(`Submitted ${routed} script${routed === 1 ? '' : 's'} to production.`);
      } else if (routed === 0) {
        alert(`No scripts were submitted.\n\nFirst failure: ${failures[0].label} — ${failures[0].detail}`);
      } else {
        alert(`Partial: ${routed} submitted, ${failures.length} failed.\n\nFirst failure: ${failures[0].label} — ${failures[0].detail}`);
      }
    } finally {
      setRoutingAll(false);
    }
  };

  const toggleAssemblyHook = (elementId) => {
    setAssemblyHooks(prev => prev.includes(elementId) ? prev.filter(id => id !== elementId) : [...prev, elementId]);
  };

  // I4 — one script's body + one CTA, tested against every selected hook.
  // Calls the existing combine endpoint once per selected hook so N hooks
  // produce N creatives sharing the same body + CTA.
  const runAssembly = async () => {
    if (!assemblyBody || !assemblyCta || assemblyHooks.length === 0) {
      alert('Pick a script/body, a CTA, and at least one hook to test.');
      return;
    }
    setAssembling(true);
    const created = [];
    const failures = [];
    try {
      for (const hookId of assemblyHooks) {
        try {
          const response = await axios.post(`${API}/offers/${offer.offer_id}/creatives/combine`, {
            hook_element_id: hookId,
            body_element_id: assemblyBody,
            cta_element_id: assemblyCta,
            channel: assemblyChannel,
            creative_type: assemblyType,
          });
          created.push(response.data);
        } catch (err) {
          failures.push(err.response?.data?.detail || err.message);
        }
      }
      setAssembledCreatives(prev => [...created, ...prev]);
      if (failures.length > 0) {
        alert(`${created.length} creative(s) assembled, ${failures.length} failed.\n${failures[0]}`);
      }
    } finally {
      setAssembling(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-emerald-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="creative-elements-step">
      <div>
        <h3 className="text-lg font-semibold text-white">Creative Elements</h3>
        <p className="text-slate-400 text-sm">Set the production type per script, assemble modular hook/CTA combinations, and submit to production</p>
      </div>

      {/* RULE 10: a failed script read is not an empty session. */}
      {scriptsState === 'error' && (
        <div className="bg-slate-900/30 border border-amber-800/50 rounded-xl p-4" data-testid="elements-scripts-load-failed">
          <p className="text-amber-400 text-sm">Could not load this session's scripts</p>
          <p className="text-slate-500 text-xs mt-1">
            The request failed. This is not the same as the session having no
            scripts — reload the step, and if it keeps failing the scripts API
            is unavailable.
          </p>
        </div>
      )}

      {scriptsState === 'ok' && scripts.length === 0 && (
        <div className="bg-slate-900/30 border border-slate-800 rounded-xl p-4" data-testid="elements-scripts-empty">
          <p className="text-slate-400 text-sm">No scripts in this session yet</p>
          <p className="text-slate-500 text-xs mt-1">
            Loaded successfully and found none for this session. Generate scripts
            in the Script stage and they will appear here.
          </p>
          <HiddenRowsNote {...hiddenScripts} testid="elements-hidden-scripts-empty" />
        </div>
      )}

      {/* Creative type per script */}
      {scripts.length > 0 && (
        <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-4" data-testid="creative-type-section">
          <h4 className="text-white font-medium mb-3">Creative Type</h4>
          <div className="space-y-2">
            {scripts.map(script => (
              <div key={script.script_id} className="flex items-center justify-between bg-slate-800/50 rounded-lg px-4 py-2.5">
                <div className="flex items-center gap-3">
                  <span className="text-white font-mono text-sm">{script.body_element_number || script.script_id?.slice(0, 10)}</span>
                  <span className="text-slate-500 text-xs">{script.platform}</span>
                  {script.production_id && <span className="px-2 py-0.5 text-xs bg-slate-700 text-slate-400 rounded">Routed</span>}
                </div>
                <select
                  value={
                    script.creative_type === 'AI'
                      ? `AI:${script.production_destination || 'TRELLO'}`
                      : (script.creative_type || '')
                  }
                  onChange={(e) => setCreativeType(script.script_id, e.target.value)}
                  disabled={settingType === script.script_id || !!script.production_id}
                  className="bg-slate-800 border border-slate-700 rounded px-2 py-1 text-sm text-white disabled:opacity-50"
                  data-testid={`creative-type-select-${script.script_id}`}
                >
                  <option value="">Set Type...</option>
                  {/* AI Video — one type, two destinations. TRELLO is the live
                      path and is what this option has always done; COMFY is a
                      deliberately unwired placeholder and says so in its own
                      label as well as refusing when routed. */}
                  <optgroup label="AI Video">
                    <option value="AI:TRELLO">AI VIDEO TRELLO</option>
                    <option value="AI:COMFY">AI VIDEO COMFY (DO NOT USE)</option>
                  </optgroup>
                  <option value="VE">VE (Video Editor)</option>
                  <option value="UGC">UGC (Creator)</option>
                </select>
                {/* Rule 10, said before the operator commits rather than only
                    after. Routing also refuses server-side — this notice is the
                    early warning, not the guard. */}
                {script.creative_type === 'AI' && script.production_destination === 'COMFY' && (
                  <p
                    className="text-xs text-amber-400 mt-1"
                    data-testid={`comfy-not-connected-${script.script_id}`}
                  >
                    AI VIDEO COMFY is not connected yet — nothing will be generated
                    or sent. Choose AI VIDEO TRELLO to send this brief to the editor.
                  </p>
                )}
              </div>
            ))}
          </div>
          {needsType.length > 0 && (
            <p className="text-slate-500 text-xs mt-3">{needsType.length} script{needsType.length > 1 ? 's' : ''} still need a creative type before they can be submitted.</p>
          )}
          {/* What is NOT on screen, and why. A filter nobody is told about is
              indistinguishable from data that does not exist. */}
          <HiddenRowsNote {...hiddenScripts} testid="elements-hidden-scripts" />
        </div>
      )}

      {/* Submit to production */}
      {unroutedScripts.length > 0 && (
        <div className="bg-emerald-500/5 border border-emerald-500/30 rounded-xl p-5" data-testid="submit-to-production-section">
          <h4 className="text-emerald-400 font-medium mb-1">Ready to Submit ({unroutedScripts.length} scripts)</h4>
          <p className="text-slate-500 text-xs mb-4">Submit to start video generation, Trello card creation, and Drive uploads.</p>
          <div className="space-y-2 mb-5">
            {unroutedScripts.map(script => (
              <div key={script.script_id} className="flex items-center justify-between bg-slate-800/50 rounded-lg px-4 py-3" data-testid={`submit-script-${script.script_id}`}>
                <div className="flex items-center gap-3">
                  <span className="text-white font-mono text-sm">{script.body_element_number || script.script_id?.slice(0, 10)}</span>
                  <span className="text-slate-400 text-sm">{script.platform} &bull; {script.total_duration_seconds}s</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`px-2.5 py-1 text-xs rounded font-medium ${
                    script.creative_type === 'AI' ? 'bg-purple-500/20 text-purple-400' :
                    script.creative_type === 'VE' ? 'bg-blue-500/20 text-blue-400' :
                    'bg-pink-500/20 text-pink-400'
                  }`}>{script.creative_type === 'AI' ? 'AI Video' : script.creative_type}</span>
                  <button
                    onClick={() => routeToProduction(script.script_id, script.creative_type)}
                    disabled={routingScript === script.script_id}
                    className="px-3 py-1 text-xs bg-slate-700 text-slate-300 rounded hover:bg-slate-600 disabled:opacity-50"
                  >
                    {routingScript === script.script_id ? '...' : 'Submit'}
                  </button>
                </div>
              </div>
            ))}
          </div>
          <button
            onClick={submitAllToProduction}
            disabled={routingAll || unroutedScripts.length === 0}
            className="w-full py-3 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 disabled:opacity-50 font-medium"
            data-testid="submit-all-to-production-btn"
          >
            {routingAll ? 'Submitting...' : `Submit All ${unroutedScripts.length} Scripts to Production`}
          </button>
        </div>
      )}

      {/* I4 — Modular assembly: many hooks against one body + one CTA */}
      <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-4" data-testid="modular-assembly-section">
        <div className="flex items-center justify-between">
          <div>
            <h4 className="text-white font-medium">Modular Assembly</h4>
            <p className="text-slate-500 text-xs mt-0.5">Build a hook test: one script and one CTA, tested against several hooks</p>
          </div>
          <button
            onClick={() => setAssemblyOpen(o => !o)}
            className="px-3 py-1.5 text-xs bg-slate-700 text-slate-300 rounded hover:bg-slate-600"
            data-testid="modular-assembly-toggle"
          >
            {assemblyOpen ? 'Close' : 'Open'}
          </button>
        </div>

        {assemblyOpen && (
          <div className="mt-4 space-y-3">
            {/* ITEM 10 — the panel said WHAT it did and never WHY. Four controls
                and an "Assemble" button is not an explanation. */}
            <div className="bg-slate-800/40 border border-slate-700/50 rounded-lg px-3 py-2.5" data-testid="modular-assembly-example">
              <p className="text-slate-300 text-xs font-medium mb-1">What this produces</p>
              <p className="text-slate-400 text-xs leading-relaxed">
                Pick one script body and one CTA, then tick several hooks. You get
                one creative per hook, <strong className="text-slate-200">identical except for the hook</strong> —
                so when they run, the difference in performance is the hook and
                nothing else.
              </p>
              <p className="text-slate-500 text-xs mt-1.5">
                Example: body <span className="font-mono text-slate-400">B13</span>{' '}
                + CTA <span className="font-mono text-slate-400">C37</span>{' '}
                + hooks <span className="font-mono text-slate-400">H34, H35, H36</span>{' '}
                &rarr; three creatives. Whichever wins tells you which hook works;
                the body and CTA are held constant so they cannot take the credit.
              </p>
            </div>
            {libraryState === 'error' ? (
              <p className="text-amber-400 text-sm" data-testid="element-library-load-failed">
                Could not load the copy library. The request failed — this is not
                the same as the library being empty. Reload the step; if it keeps
                failing the copy-elements API is unavailable.
              </p>
            ) : (hookElements.length === 0 || bodyElements.length === 0 || ctaElements.length === 0) ? (
              <p className="text-slate-500 text-sm" data-testid="element-library-genuinely-empty">
                Loaded the copy library and it does not yet hold one of each part
                ({hookElements.length} hook{hookElements.length === 1 ? '' : 's'},
                {' '}{bodyElements.length} bod{bodyElements.length === 1 ? 'y' : 'ies'},
                {' '}{ctaElements.length} CTA{ctaElements.length === 1 ? '' : 's'}).
                Elements are created as hooks, scripts and CTAs are generated earlier in this session.
              </p>
            ) : (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-slate-400 mb-1">Script / Body</label>
                    <select
                      value={assemblyBody}
                      onChange={(e) => setAssemblyBody(e.target.value)}
                      className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm"
                      data-testid="assembly-body-select"
                    >
                      <option value="">Select a script...</option>
                      {bodyElements.map(el => (
                        <option key={el.element_id} value={el.element_id}>{el.element_number || el.element_id}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-slate-400 mb-1">CTA</label>
                    <select
                      value={assemblyCta}
                      onChange={(e) => setAssemblyCta(e.target.value)}
                      className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm"
                      data-testid="assembly-cta-select"
                    >
                      <option value="">Select a CTA...</option>
                      {ctaElements.map(el => (
                        <option key={el.element_id} value={el.element_id}>{el.element_number || el.element_id}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-xs text-slate-400 mb-1.5">Hooks to test ({assemblyHooks.length} selected)</label>
                  <div className="flex flex-wrap gap-2" data-testid="assembly-hooks-list">
                    {hookElements.map(el => (
                      <button
                        key={el.element_id}
                        type="button"
                        onClick={() => toggleAssemblyHook(el.element_id)}
                        className={`px-2.5 py-1 text-xs rounded-full border ${
                          assemblyHooks.includes(el.element_id)
                            ? 'bg-emerald-500/15 text-emerald-400 border-emerald-500/40'
                            : 'bg-slate-800 text-slate-400 border-slate-700 hover:border-slate-500'
                        }`}
                        data-testid={`assembly-hook-${el.element_id}`}
                      >
                        {el.element_number || el.element_id}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-slate-400 mb-1">Channel</label>
                    <select
                      value={assemblyChannel}
                      onChange={(e) => setAssemblyChannel(e.target.value)}
                      className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm"
                    >
                      <option value="META">Meta</option>
                      <option value="TIKTOK">TikTok</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-slate-400 mb-1">Creative Type</label>
                    <select
                      value={assemblyType}
                      onChange={(e) => setAssemblyType(e.target.value)}
                      className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm"
                    >
                      <option value="AI">AI</option>
                      <option value="VE">VE</option>
                      <option value="UGC">UGC</option>
                    </select>
                  </div>
                </div>

                <button
                  onClick={runAssembly}
                  disabled={assembling || !assemblyBody || !assemblyCta || assemblyHooks.length === 0}
                  className="w-full py-2 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 disabled:opacity-50 text-sm font-medium"
                  data-testid="run-assembly-btn"
                >
                  {assembling ? 'Assembling…' : `Assemble ${assemblyHooks.length || ''} Creative${assemblyHooks.length === 1 ? '' : 's'}`}
                </button>

                {assembledCreatives.length > 0 && (
                  <div className="pt-2 border-t border-slate-800" data-testid="assembled-creatives-list">
                    <p className="text-slate-400 text-xs mb-2">{assembledCreatives.length} creative(s) assembled this session</p>
                    <div className="space-y-1">
                      {assembledCreatives.map((c, i) => (
                        <p key={i} className="text-white text-xs font-mono">{c.creative_identifier || c.creative_id || JSON.stringify(c).slice(0, 60)}</p>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>

      <div className="flex justify-end pt-4">
        <button
          onClick={onComplete}
          className="px-6 py-2 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600"
          data-testid="continue-to-prompts-btn"
        >
          Continue to Prompts
        </button>
      </div>
    </div>
  );
};

// Main Creative Engine Component
const CreativeEnginePage = ({ offer: initialOffer, onClose, sessionId }) => {
  const [offer, setOffer] = useState(initialOffer);
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  // Bumped each time the chat-bridge locks a new artifact — step components
  // include this in their fetch deps so the new chat-derived item shows up
  // without a manual refresh. Manual Session Mode (Phase 8).
  const [lockRefreshKey, setLockRefreshKey] = useState(0);
  const handleLockSuccess = useCallback(() => {
    setLockRefreshKey(k => k + 1);
  }, []);
  // Multi-angle state
  // `multiAngles` / `currentAngleIdx` removed 2026-09-09 (MA-3) — see the
  // tombstone above `goToNextStep`. Each angle is a real session now.

  // 7-stage restructure (I1). Indices are load-bearing — determineInitialStep,
  // the multi-angle loop, and the session step-label patch below all key off
  // them, so a stage insertion/removal must update all three together.
  // The indices and the stage list come from the shared module now, so v1 and
  // v2 cannot disagree about what step 4 is.
  const steps = SHARED_STEPS;

  // Parse URL once — memoized so stale query strings don't re-trigger
  // the initial-step determination effect on re-render.
  const { isNewSession, sessionStartTime } = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    const isNew = params.get('new') === '1';
    return {
      isNewSession: isNew,
      sessionStartTime: isNew ? new Date().toISOString() : null,
    };
  }, []);

  // Step 0 gate: a brand-new affiliate session opens in the Exploration phase
  // first (skippable). onSkip/onProceed drop into the existing step wizard at
  // Psychology Map (step 0). Resumed sessions never see the gate. ABH never
  // reaches this component (it uses ABHCreativeEngine) — BU guard is defensive.
  const [showExploration, setShowExploration] = useState(
    () => isNewSession && !!sessionId && offer.business_unit_id !== ABH_BU_ID
  );

  const refreshOffer = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/offers/${offer.offer_id}`);
      setOffer(response.data);
    } catch (error) {
      console.error('Failed to refresh offer:', error);
    }
  }, [offer.offer_id]);

  // Determine the opening step ONCE per offer on mount, through the SHARED
  // resolver (`services/creativeSessionStep`). A ref guard stops downstream
  // offer changes (psychology_map_confirmed flipping true, selected_angle_id
  // being set) from re-running this and clobbering in-progress navigation.
  //
  // MOVED, NOT REWRITTEN (V2-AUTONOMOUS-WAVE2B). v2 mounts the same resolver.
  // Two implementations of "which step am I on" would be two answers to a
  // question that has one, against a single database, while both versions run.
  //
  // The rule also got SMALLER and more correct on the way: it now reads the
  // step the server already stores. It used to ignore that and re-derive a
  // position from OFFER-scoped queries, so any production anywhere on the offer
  // sent the operator to Prompts. Measured 2026-09-09: 89 sessions across 13
  // offers resumed at step 7 no matter where their operator actually was.
  const initialStepDeterminedRef = useRef(false);
  const [stepSource, setStepSource] = useState(null);
  const [stepWarnings, setStepWarnings] = useState([]);
  useEffect(() => {
    if (initialStepDeterminedRef.current) return;
    initialStepDeterminedRef.current = true;

    const run = async () => {
      let session = null;
      if (sessionId && !isNewSession) {
        try {
          const res = await axios.get(`${API}/creative/sessions/${sessionId}`);
          session = res.data?.session || res.data || null;
        } catch (e) {
          // Reported by the resolver as a warning rather than silently
          // becoming "no stored step".
          session = null;
        }
      }
      const rows = (r, key) => (r?.data?.[key] || []);
      const { step, source, warnings } = await resolveInitialStep({
        isNewSession,
        session,
        offer,
        fetchers: {
          productions: async () =>
            rows(await axios.get(`${API}/offers/${offer.offer_id}/productions`), 'productions'),
          approvedScripts: async () =>
            rows(await axios.get(`${API}/offers/${offer.offer_id}/scripts/approved-unrouted`), 'scripts'),
          scripts: async () =>
            rows(await axios.get(`${API}/offers/${offer.offer_id}/scripts`), 'scripts'),
          hooks: async () =>
            rows(await axios.get(`${API}/offers/${offer.offer_id}/hooks`), 'hooks'),
        },
      });
      setCurrentStep(step);
      setStepSource(source);
      setStepWarnings(warnings);
    };

    run();
    refreshOffer();
  }, [isNewSession, sessionId, offer, refreshOffer]);

  /* ── The in-memory multi-angle loop — REMOVED 2026-09-09, MA-3 ────────────
     It ran Hooks -> Scripts, then jumped back to Hooks for the next angle. It
     was wrong in four ways, and the ruling replaced rather than repaired it:

       · It covered TWO of the five steps the Founder ruled each angle must
         have. Its own comment said CTA, Creative Elements and Prompts were
         "shared, not per-angle" — they are not.
       · It lived in React state, so a reload lost the queue entirely.
       · Its failure path was `console.error`, which left the operator writing
         hooks for angle 1 while the header said angle 2.
       · It re-pointed `offers.selected_angle_id` on every hop, which is the
         offer-wide pointer several other readers use.

     Each angle is now a REAL SESSION (MA-1), cut by the Angle step (MA-3), so
     there is nothing to loop through: this session runs one angle to
     completion, and its siblings are sessions in their own right.

     Rule 8 clearance before removing:
       `multiAngles` / `currentAngleIdx`  this component only; both removed here
       `handleMultiAngleSelect`           this component only; removed here
       `onMultiAngleSelect` PROP          KEPT on `AngleSelectionStep`. It is
                                          part of the shared `useAngles` hook's
                                          contract, optional, and calling it
                                          with no handler is a no-op. Removing a
                                          hook's callback because one caller
                                          stopped passing it is the deletion
                                          rule 8 exists to stop.
     ────────────────────────────────────────────────────────────────────── */
  // WHICH TRACK THIS SESSION IS, read once for the whole wizard so every step
  // shows it — the Founder's requirement, and the thing v1 had none of after
  // MA-3 removed the old banner without giving v1 the replacement.
  const T = useSessionTracks(sessionId, lockRefreshKey);

  // AUTONOMOUS-WIZARD-THREE: video format and creative type, asked ONCE on
  // step 1 exactly as a Custom session asks them. The session response carries
  // both the options and whatever was already chosen.
  const [sessionMeta, setSessionMeta] = useState(null);
  const [metaError, setMetaError] = useState(null);
  useEffect(() => {
    if (!sessionId) return undefined;
    let live = true;
    axios.get(`${API}/creative/sessions/${sessionId}`)
      .then((r) => { if (live) { setSessionMeta(r.data); setMetaError(null); } })
      // Not swallowed: without this the picker would render two empty dropdowns
      // and look like a choice nobody made, rather than a read that failed.
      .catch((e) => { if (live) setMetaError(errorText(e)); });
    return () => { live = false; };
  }, [sessionId]);

  const saveSessionChoice = async (patch) => {
    setSessionMeta((prev) => ({ ...(prev || {}), ...patch }));
    try {
      await axios.patch(`${API}/creative/sessions/${sessionId}`, patch);
      setMetaError(null);
    } catch (e) {
      setMetaError(errorText(e));
    }
  };

  const goToNextStep = async () => {
    await refreshOffer();
    setCurrentStep(prev => Math.min(prev + 1, steps.length - 1));
  };

  /** Open a sibling track. It is a real session, so this is navigation.
   *
   * `window.location` rather than a router hook: this module is mounted by
   * `CreativeEngineWrapper` (which owns the route) and imports no router today.
   * Adding a router dependency to a 3,400-line component being retired in wave
   * 5 buys nothing — and a full load is the honest behaviour here anyway, since
   * every step is remounting against a different session.
   */
  const openTrack = (siblingId) => {
    window.location.href =
      `/creative-engine/${offer.offer_id}?session=${siblingId}`;
  };

  // Update session step when step changes
  useEffect(() => {
    if (sessionId) {
      const stepLabels = {
        0: 'Psychology Map', 1: 'Angle Selection', 2: 'Hook Generation',
        3: 'Script Generation', 4: 'CTA', 5: 'Creative Elements', 6: 'Prompts',
      };
      axios.patch(`${API}/creative/sessions/${sessionId}`, {
        current_step: currentStep,
        step_label: stepLabels[currentStep] || `Step ${currentStep}`,
      }).catch(() => {});
    }
  }, [currentStep, sessionId]);

  // Gather current step content for chat context
  const getStepContent = useCallback(() => {
    const content = {};
    if (offer.psychology_map) content.psychology_map = offer.psychology_map;
    if (offer.selected_angle_id) content.selected_angle = offer.selected_angle_id;
    return content;
  }, [offer]);

  return (
    <div className="space-y-6" data-testid="creative-engine-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white" data-testid="creative-engine-title">Creative Engine</h2>
          <p className="text-slate-400" data-testid="offer-name">{offer.name}</p>
        </div>
        {onClose && (
          <button
            onClick={onClose}
            data-testid="close-creative-engine-btn"
            className="px-4 py-2 bg-slate-700 text-slate-300 rounded-lg hover:bg-slate-600"
          >
            Close
          </button>
        )}
      </div>

      {/* Step 0 — Exploration gate (pre-Psychology-Map; skippable; affiliate-only) */}
      {showExploration && (
        <ExplorationPhase
          sessionId={sessionId}
          offer={offer}
          onProceed={() => setShowExploration(false)}
          onSkip={() => setShowExploration(false)}
        />
      )}

      {!showExploration && (
      <>
      {/* Step Indicator */}
      <StepIndicator currentStep={currentStep} steps={steps} />

      {/* The multi-angle progress banner went with the loop (MA-3,
          2026-09-09). It read from `multiAngles` / `currentAngleIdx`, which no
          longer exist, and it described a run that now happens across separate
          sessions rather than inside this one. v2's `TrackSwitcher` is the
          replacement, and it navigates between the real sessions instead of
          drawing dots for an in-memory queue.
          data-testid removed with it: `multi-angle-progress`, asserted by no
          test. */}

      {/* THE TRACK INDICATOR, on every step — not just Angle Selection.
          A track is a real session, so the tabs navigate. Renders nothing at
          all for a session that is not part of a set. */}
      <TrackSwitcher tracks={T.tracks} currentSessionId={sessionId} onOpen={openTrack} />

      {/* Step 1 asks for format and type, once, for the whole run — the same
          question a Custom session asks on its first screen. */}
      {currentStep === 0 && sessionId && (
        <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-4 mb-4"
             data-testid="autonomous-format-type">
          <p className="text-sm font-medium text-slate-300 mb-2">
            How will this creative be made?
          </p>
          {metaError && (
            <p className="text-amber-400 text-xs mb-2" role="alert"
               data-testid="autonomous-format-type-error">
              {metaError}
            </p>
          )}
          <FormatTypePicker
            videoFormat={sessionMeta?.video_format}
            creativeType={sessionMeta?.creative_type}
            formatOptions={sessionMeta?.video_format_options || []}
            typeOptions={sessionMeta?.creative_type_options || []}
            onFormatChange={(v) => saveSessionChoice({ video_format: v })}
            onTypeChange={(v) => saveSessionChoice({ creative_type: v })}
            testidPrefix="autonomous-ft"
          />
          <p className="text-slate-500 text-xs mt-2">
            Chosen once for this session. Every script it generates starts from
            this type; you can still change one on Creative Elements.
          </p>
        </div>
      )}

      {/* Main Layout: Step Content + Chat Panel */}
      <div className={`grid gap-6 ${sessionId ? 'grid-cols-1 lg:grid-cols-[1fr_380px]' : 'grid-cols-1'}`}>
        {/* Step Content */}
        <div>
          <div className="bg-slate-900/30 border border-slate-800 rounded-xl p-6" data-testid="step-content-container">
            {currentStep === STEP_PSYCHOLOGY && (
              <PsychologyMapStep
                offer={offer} 
                onComplete={goToNextStep}
                onRefresh={refreshOffer}
                sessionId={sessionId}
                isNewSession={isNewSession}
              />
            )}
            {currentStep === 1 && (
              <AngleSelectionStep
                offer={offer}
                onComplete={goToNextStep}
                sessionId={sessionId}
                isNewSession={isNewSession}
                sessionStartTime={sessionStartTime}
                refreshKey={lockRefreshKey}
              />
            )}
            {currentStep === 2 && (
              <HookSelectionStep
                offer={offer}
                angleId={T.angleId}
                onComplete={goToNextStep}
                sessionId={sessionId}
                isNewSession={isNewSession}
                refreshKey={lockRefreshKey}
                              sessionStartTime={sessionStartTime}
                />
            )}
            {currentStep === 3 && (
              <ScriptGenerationStep
                offer={offer}
                angleId={T.angleId}
                onComplete={goToNextStep}
                sessionId={sessionId}
                isNewSession={isNewSession}
                sessionStartTime={sessionStartTime}
                refreshKey={lockRefreshKey}
              />
            )}
            {currentStep === STEP_CTA && (
              <CTAStep
                offer={offer}
                onComplete={goToNextStep}
                sessionId={sessionId}
                isNewSession={isNewSession}
                sessionStartTime={sessionStartTime}
                refreshKey={lockRefreshKey}
              />
            )}
            {currentStep === STEP_ELEMENTS && (
              <CreativeElementsStep
                offer={offer}
                onComplete={goToNextStep}
                sessionId={sessionId}
                isNewSession={isNewSession}
                sessionStartTime={sessionStartTime}
                refreshKey={lockRefreshKey}
              />
            )}
            {currentStep === STEP_PROMPTS && (
              <PromptsStep offer={offer} sessionId={sessionId} isNewSession={isNewSession} sessionStartTime={sessionStartTime} />
            )}
          </div>

          {/* Step Navigation */}
          <div className="flex justify-between mt-6" data-testid="step-navigation">
            <button
              onClick={() => setCurrentStep(prev => Math.max(prev - 1, 0))}
              disabled={currentStep === 0}
              data-testid="previous-step-btn"
              className="px-4 py-2 bg-slate-700 text-slate-300 rounded-lg hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <div className="flex gap-2">
              {steps.map((step, idx) => (
                  <button
                    key={step.id}
                    onClick={() => setCurrentStep(idx)}
                    data-testid={`step-indicator-btn-${idx}`}
                    className={`w-8 h-8 rounded-full text-sm ${
                      idx === currentStep 
                        ? 'bg-emerald-500 text-white' 
                        : 'bg-slate-700 text-slate-400 hover:bg-slate-600'
                    }`}
                  >
                    {idx + 1}
                  </button>
              ))}
            </div>
            <button
              onClick={() => {
                setCurrentStep(prev => Math.min(prev + 1, steps.length - 1));
              }}
              disabled={currentStep === steps.length - 1}
              data-testid="next-step-btn"
              className="px-4 py-2 bg-slate-700 text-slate-300 rounded-lg hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>

        {/* Chat Panel (only visible when session is active) */}
        {sessionId && (
          <div className="lg:sticky lg:top-4 lg:self-start">
            <ChatPanel
              sessionId={sessionId}
              currentStep={currentStep}
              offer={offer}
              getStepContent={getStepContent}
              onLockSuccess={handleLockSuccess}
            />
          </div>
        )}
      </div>
      </>
      )}
    </div>
  );
};

export default CreativeEnginePage;
